import { useEffect, useState, useRef, useCallback } from "react";
import { api, formatApiError, buildAssetUrl, SUPPORTED_CURRENCIES } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import { CONNECT_ENABLED } from "@/lib/featureFlags";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Eye, EyeOff, X, ShieldAlert, Clipboard, CheckCircle2, XCircle, ExternalLink, Loader2, RefreshCcw, ChevronDown, ChevronUp, GripVertical, Clock, MapPin, User, Coins, Building2, Briefcase, Hash, MessageSquare } from "lucide-react";
import { TagChip } from "@/components/TagChip";

const defaultData = {
  company: {
    name: "", address: "", phone: "", email: "", vat_number: "", logo_url: "",
    // Phase P.26 — structured billing address; loaded from settings.company
    // on mount. Defaults to Ireland so first-run modals default sensibly.
    country: "IE", address_line1: "", city: "", postal_code: "",
  },
  invoice_template: {
    header_text: "", footer_text: "", extra_fields: [], accounts_dept_email: "",
    header_markdown: "", body_intro_markdown: "", body_outro_markdown: "", footer_markdown: "",
    payment_terms: "", bank_details: "",
    accent_color: "#0ea5e9", logo_position: "top-left",
    show_vat_block: true, show_extra_fields: true,
  },
  api_keys: { resend_api_key: "", ors_api_key: "" },
  default_vat_rate: 23.0,
  currency: "EUR",
  audit_mode: false,
  require_location: true,
  from_email: "",
  staff_card_fields: ["time", "customer_name", "address", "payment_form"],
  calendar_display: {
    fields: ["customer_name", "job_template", "time_start", "payment_type", "payment_status"],
  },
  booking_counter: 0,
  invoice_counter: 0,
};

const TOKEN_LIST = [
  { token: "company_name", label: "Company name" },
  { token: "company_address", label: "Company address" },
  { token: "company_vat_number", label: "Company VAT" },
  // Phase 38 — Company contact + branding tokens
  { token: "company_phone", label: "Company phone" },
  { token: "company_email", label: "Company email" },
  { token: "company_mobile", label: "Company mobile" },
  { token: "company_website", label: "Company website" },
  { token: "company_registration_number", label: "Company registration number" },
  { token: "company_tagline", label: "Company tagline" },
  { token: "customer_company", label: "Customer company" },
  { token: "customer_contact", label: "Customer contact" },
  { token: "customer_address", label: "Customer address" },
  { token: "customer_vat_number", label: "Customer VAT" },
  { token: "invoice_number", label: "Invoice number" },
  { token: "booking_code", label: "Booking code" },
  { token: "issued_date", label: "Issued date" },
  { token: "due_date", label: "Due date (+30d)" },
  { token: "subtotal_before_discount", label: "Subtotal before discount (formatted)" },
  { token: "subtotal_before_discount_amount", label: "Subtotal before discount (number only)" },
  { token: "subtotal", label: "Subtotal after discount (formatted)" },
  { token: "subtotal_amount", label: "Subtotal after discount (number only)" },
  { token: "discount_type", label: "Discount type (none/amount/percent)" },
  { token: "discount_value", label: "Discount value (raw)" },
  { token: "discount_amount", label: "Discount (formatted)" },
  { token: "discount_amount_value", label: "Discount (number only)" },
  { token: "tax_amount", label: "Tax (formatted)" },
  { token: "tax_amount_value", label: "Tax (number only)" },
  { token: "tax_rate", label: "Tax rate %" },
  { token: "total", label: "Total (formatted)" },
  { token: "total_amount", label: "Total (number only)" },
  { token: "currency_symbol", label: "Currency symbol" },
  { token: "currency_code", label: "Currency code" },
  { token: "payment_type_name", label: "Payment type name" },
  { token: "paid_method_name", label: "Paid method (when paid)" },
  { token: "staff_name", label: "Staff name" },
];

function MaskedInput({ value, onChange, testId, placeholder }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <Input
        type={shown ? "text" : "password"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="pr-10"
      />
      <button type="button" onClick={() => setShown((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid={`${testId}-toggle`}>
        {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

// Phase 44 — branded QR live preview. Fetches `/settings/qr-preview` on
// mount AND any time brand-accent / logo change so the manager can see
// what their invoice QR will look like before they hit Save.
function QrPreviewBlock({ brandAccentColor, hasLogo }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  // Re-fetch debounced when accent color changes (the server reads the
  // *persisted* settings, so the manager must Save before this updates).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get("/settings/qr-preview");
        if (!cancelled) setPreview(d);
      } catch {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brandAccentColor, hasLogo]);
  return (
    <div className="border-t border-border pt-4 mt-2" data-testid="settings-qr-preview-block">
      <div className="text-[11px] mono uppercase tracking-wider text-muted-foreground mb-2">QR preview</div>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-32 w-32 border border-border rounded-[6px] bg-white grid place-items-center p-2">
          {loading ? (
            <span className="text-[10px] text-muted-foreground">Loading…</span>
          ) : preview?.data_uri ? (
            <img src={preview.data_uri} alt="QR preview" className="max-h-full max-w-full" data-testid="settings-qr-preview-img" />
          ) : (
            <span className="text-[10px] text-muted-foreground">No preview</span>
          )}
        </div>
        <div className="flex-1 min-w-[200px] text-[11px] space-y-1">
          <div className="flex items-center gap-2">
            <span className="mono uppercase tracking-wider text-muted-foreground w-20">Accent</span>
            {preview?.accent_color_applied ? (
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-3 w-3 rounded border border-border" style={{ background: preview.accent_color_applied }} />
                <code className="mono">{preview.accent_color_applied}</code>
              </span>
            ) : (
              <span className="text-amber-700" data-testid="settings-qr-preview-fallback">
                Black fallback {preview?.accent_color_fallback_reason === "low_contrast" ? "(contrast too low against white)" : ""}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="mono uppercase tracking-wider text-muted-foreground w-20">Logo</span>
            <span>{preview?.has_logo ? "Embedded" : "Not uploaded yet"}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="mono uppercase tracking-wider text-muted-foreground w-20">Encodes</span>
            <span className="font-mono truncate max-w-[260px]" title={preview?.url}>{preview?.url}</span>
          </div>
          <p className="text-muted-foreground pt-1">
            Save Settings to refresh the preview after editing.
          </p>
        </div>
      </div>
    </div>
  );
}

// Phase B — collapsible section wrapper. Always collapsed on fresh open
// (we deliberately do NOT persist state across reloads — keeps the page
// predictable and avoids localStorage bloat). Multiple sections can be
// expanded simultaneously (no accordion exclusivity).
function SettingsSection({ slug, title, subtitle, defaultOpen = false, children }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <section
      className="bg-card border border-border rounded-[8px] overflow-hidden"
      data-testid={`settings-section-${slug}`}
    >
      <button
        type="button"
        className="w-full flex items-center justify-between gap-4 px-5 py-4 hover:bg-secondary/40 transition-colors text-left"
        onClick={() => setOpen((o) => !o)}
        data-testid={`settings-section-${slug}-toggle`}
        aria-expanded={open}
      >
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-extrabold truncate">{title}</h2>
          {subtitle && (
            <p className="text-[12px] text-muted-foreground mt-0.5 truncate">{subtitle}</p>
          )}
        </div>
        {open ? (
          <ChevronUp size={18} strokeWidth={2.2} className="shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown size={18} strokeWidth={2.2} className="shrink-0 text-muted-foreground" />
        )}
      </button>
      {open && (
        <div
          className="px-5 pb-5 pt-1 border-t border-border space-y-6 animate-in fade-in slide-in-from-top-1 duration-200"
          data-testid={`settings-section-${slug}-content`}
        >
          {children}
        </div>
      )}
    </section>
  );
}


// Phase B — Calendar display config. Drag-drop ordered list of 1..5
// fields the manager wants on every booking card on the calendar. Live
// preview tile sits below so the effect is visible before Save.
const CALENDAR_FIELD_CATALOG = [
  { code: "customer_name",     label: "Customer name",   Icon: User },
  { code: "company_name",      label: "Company name",    Icon: Building2 },
  { code: "job_template",      label: "Job template",    Icon: Briefcase },
  { code: "payment_type",      label: "Payment type",    Icon: Coins },
  { code: "time_start",        label: "Start time",      Icon: Clock },
  { code: "location_postcode", label: "Postcode",        Icon: MapPin },
  { code: "staff_assigned",    label: "Staff",           Icon: User },
  { code: "payment_status",    label: "Payment status",  Icon: CheckCircle2 },
  { code: "notes_truncated",   label: "Notes (preview)", Icon: MessageSquare },
  { code: "booking_code",      label: "Booking code",    Icon: Hash },
];
const SAMPLE_BOOKING = {
  customer_name: "Sarah O'Brien",
  customer_company_name: "Acme Construction Ltd",
  template_name: "Pump Hire + Piping",
  payment_type_name: "Cash + Invoice",
  time_start: "09:30",
  postcode: "D02 X285",
  staff_name: "John",
  payment_status: "Awaiting payment",
  notes_truncated: "Side gate access only — call on arrival",
  booking_code: "B-000142",
};
function previewValueForField(code) {
  switch (code) {
    case "customer_name":     return SAMPLE_BOOKING.customer_name;
    case "company_name":      return SAMPLE_BOOKING.customer_company_name;
    case "job_template":      return SAMPLE_BOOKING.template_name;
    case "payment_type":      return SAMPLE_BOOKING.payment_type_name;
    case "time_start":        return SAMPLE_BOOKING.time_start;
    case "location_postcode": return SAMPLE_BOOKING.postcode;
    case "staff_assigned":    return SAMPLE_BOOKING.staff_name;
    case "payment_status":    return SAMPLE_BOOKING.payment_status;
    case "notes_truncated":   return SAMPLE_BOOKING.notes_truncated;
    case "booking_code":      return SAMPLE_BOOKING.booking_code;
    default: return "";
  }
}
function CalendarDisplaySection({ value, onChange, disabled = false }) {
  const fields = Array.isArray(value) && value.length ? value : [];
  const available = CALENDAR_FIELD_CATALOG.filter((f) => !fields.includes(f.code));
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const moveTo = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...fields];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };
  const remove = (code) => onChange(fields.filter((c) => c !== code));
  const add = (code) => {
    if (fields.length >= 5) return;
    if (fields.includes(code)) return;
    onChange([...fields, code]);
  };
  return (
    <div className="space-y-4" data-testid="settings-calendar-display-content">
      <p className="text-[12px] text-muted-foreground">
        Pick 1 to 5 fields and drag to reorder. The order here is the order
        each booking pill on your calendar will show.
      </p>
      <ol
        className="space-y-2"
        data-testid="calendar-display-field-list"
      >
        {fields.map((code, idx) => {
          const def = CALENDAR_FIELD_CATALOG.find((f) => f.code === code);
          if (!def) return null;
          const { Icon, label } = def;
          const isDragging = dragIdx === idx;
          const isOver = overIdx === idx && dragIdx !== idx;
          return (
            <li
              key={code}
              draggable={!disabled}
              onDragStart={(e) => {
                if (disabled) return;
                setDragIdx(idx);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", String(idx));
              }}
              onDragOver={(e) => {
                if (disabled || dragIdx == null) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (overIdx !== idx) setOverIdx(idx);
              }}
              onDragLeave={() => { if (overIdx === idx) setOverIdx(null); }}
              onDrop={(e) => {
                e.preventDefault();
                const from = Number(e.dataTransfer.getData("text/plain") || dragIdx);
                moveTo(from, idx);
                setDragIdx(null);
                setOverIdx(null);
              }}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              className={`flex items-center gap-2 bg-secondary/40 border border-border rounded-[6px] px-3 py-2 transition-all ${
                isDragging ? "opacity-40" : ""
              } ${isOver ? "ring-2 ring-accent border-accent" : ""}`}
              data-testid={`calendar-display-field-row-${code}`}
            >
              <GripVertical size={14} className="text-muted-foreground cursor-grab" aria-hidden="true" />
              <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground w-5">
                {idx + 1}
              </span>
              <Icon size={14} className="text-muted-foreground shrink-0" aria-hidden="true" />
              <span className="font-semibold text-sm flex-1 truncate">{label}</span>
              <button
                type="button"
                onClick={() => remove(code)}
                disabled={disabled || fields.length <= 1}
                className="text-muted-foreground hover:text-rose-600 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`calendar-display-remove-${code}`}
                title={fields.length <= 1 ? "At least one field is required" : "Remove field"}
              >
                <X size={16} />
              </button>
            </li>
          );
        })}
      </ol>
      <div className="flex items-center gap-2 flex-wrap">
        <Select
          value=""
          onValueChange={(v) => v && add(v)}
          disabled={disabled || fields.length >= 5 || available.length === 0}
        >
          <SelectTrigger
            className="w-[220px]"
            data-testid="calendar-display-add-field"
          >
            <SelectValue placeholder={fields.length >= 5 ? "Max 5 fields reached" : "Add field…"} />
          </SelectTrigger>
          <SelectContent>
            {available.map((f) => (
              <SelectItem
                key={f.code}
                value={f.code}
                data-testid={`calendar-display-add-${f.code}`}
              >
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground">
          {fields.length}/5 selected
        </span>
      </div>

      {/* Live preview */}
      <div className="space-y-2">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-muted-foreground">Live preview</h3>
        <div
          className="max-w-xs bg-card border border-border rounded-[6px] pl-2 pr-2 py-1.5 flex flex-col gap-0.5 shadow-sm"
          style={{ borderLeftWidth: 3, borderLeftColor: "#0ea5e9" }}
          data-testid="calendar-display-preview-card"
        >
          {fields.length === 0 ? (
            <div className="text-[12px] italic text-muted-foreground">No fields selected.</div>
          ) : (
            fields.map((code) => {
              const def = CALENDAR_FIELD_CATALOG.find((f) => f.code === code);
              if (!def) return null;
              const { Icon } = def;
              return (
                <div
                  key={code}
                  className="flex items-center gap-1 min-w-0 text-[11.5px]"
                  data-testid={`calendar-display-preview-row-${code}`}
                >
                  <Icon size={11} strokeWidth={2.4} className="shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className={`truncate ${code === "time_start" ? "mono tabular-nums font-bold" : "font-medium"}`}>
                    {previewValueForField(code)}
                  </span>
                </div>
              );
            })
          )}
        </div>
        <p className="text-[11px] text-muted-foreground italic">
          Press <strong>Save changes</strong> at the top to apply across the calendar.
        </p>
      </div>
    </div>
  );
}



export default function Settings() {
  const { manager } = useAuth();
  const amOwner = manager?.role === "owner";
  const isSeedDemo = !!manager?.company_is_seed_demo;
  const [data, setData] = useState(defaultData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/settings");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setCompany = (k, v) => setData((d) => ({ ...d, company: { ...d.company, [k]: v } }));
  const setInvTpl = (k, v) => setData((d) => ({ ...d, invoice_template: { ...d.invoice_template, [k]: v } }));
  const setApiKey = (k, v) => setData((d) => ({ ...d, api_keys: { ...d.api_keys, [k]: v } }));

  const updateExtra = (i, k, v) => setData((d) => {
    const extra_fields = [...d.invoice_template.extra_fields];
    extra_fields[i] = { ...extra_fields[i], [k]: v };
    return { ...d, invoice_template: { ...d.invoice_template, extra_fields } };
  });
  const addExtra = () => setData((d) => ({
    ...d, invoice_template: { ...d.invoice_template, extra_fields: [...d.invoice_template.extra_fields, { key: "", value: "" }] }
  }));
  const removeExtra = (i) => setData((d) => ({
    ...d, invoice_template: { ...d.invoice_template, extra_fields: d.invoice_template.extra_fields.filter((_, idx) => idx !== i) }
  }));

  const save = async () => {
    setSaving(true);
    try {
      // Phase 9: invoice_template is no longer managed via Settings — it lives in /api/invoice-templates.
      const payload = {
        company: data.company,
        api_keys: data.api_keys,
        default_vat_rate: Number(data.default_vat_rate),
        currency: data.currency || "EUR",
        audit_mode: !!data.audit_mode,
        require_location: data.require_location !== false,
        from_email: data.from_email || "",
        // Phase 27 notification toggle
        pay_online_button_enabled: data.pay_online_button_enabled !== false,
        // Phase 34 reminder-tier toggles
        reminder_tier_3d_enabled: data.reminder_tier_3d_enabled !== false,
        reminder_tier_7d_enabled: data.reminder_tier_7d_enabled !== false,
        reminder_tier_14d_enabled: data.reminder_tier_14d_enabled !== false,
        // Phase 34 follow-up — per-tenant invoice overrides
        invoice_payment_terms_text: data.invoice_payment_terms_text ?? "",
        invoice_bank_name: data.invoice_bank_name ?? "",
        invoice_bank_iban: data.invoice_bank_iban ?? "",
        invoice_bank_bic: data.invoice_bank_bic ?? "",
        // Phase 40 — external payment link
        external_payment_enabled: !!data.external_payment_enabled,
        external_payment_label: data.external_payment_label ?? "",
        external_payment_url: data.external_payment_url ?? "",
        external_payment_qr_enabled: data.external_payment_qr_enabled !== false,
        external_payment_instructions: data.external_payment_instructions ?? "",
        // Phase 62.4 — "How to pay" copy used by the Send Invoice quick-insert button.
        payment_instructions: data.payment_instructions ?? "",
        // Phase 48 — soft conflict warning toggle.
        warn_on_time_conflicts: data.warn_on_time_conflicts !== false,
        // Phase 53 — month-view heat-map thresholds.
        month_heatmap_green_max: Number(data.month_heatmap_green_max ?? 3),
        month_heatmap_amber_max: Number(data.month_heatmap_amber_max ?? 5),
        // Phase B — calendar booking-card display config.
        calendar_display: data.calendar_display && Array.isArray(data.calendar_display.fields)
          ? { fields: data.calendar_display.fields }
          : undefined,
      };
      const { data: d } = await api.put("/settings", payload);
      setData(d);
      window.dispatchEvent(new Event("jobsbook:settings-changed"));
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: r } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCompany("logo_url", r.logo_url);
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="page text-muted-foreground text-sm">Loading…</div>;

  return (
    <div className="page" data-testid="settings-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// System</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Settings</h1>
        </div>
        {amOwner && (
          <Button onClick={save} disabled={saving} className="btn-cta" data-testid="settings-save-button">
            {saving ? "Saving..." : "Save changes"}
          </Button>
        )}
      </div>
      {!amOwner && (
        <div className="mt-4 bg-amber-50 border border-amber-300 text-amber-900 rounded-md p-3 text-sm" data-testid="settings-admin-readonly-banner">
          <span className="font-bold">Read-only view.</span> Only the Owner can edit company settings — contact your owner to make changes.
        </div>
      )}

      <div className="space-y-4 mt-6">
        {/* ──────────────────────────────────────────────────────────────
            Phase B — 9 collapsible sections. All collapsed by default,
            multiple can be open simultaneously. State is NOT persisted
            across reloads (deliberate — keeps the page predictable).
            ────────────────────────────────────────────────────────────── */}
        <SettingsSection slug="company" title="Company basics" subtitle="Name, address, contact, logo, brand colour">
        <section className="bg-card border border-border rounded-[8px] p-6 space-y-4" data-testid="settings-company">
          <h2 className="text-lg font-extrabold">Company details</h2>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Name" testId="company-name">
                <Input value={data.company.name} onChange={(e) => setCompany("name", e.target.value)} data-testid="company-name-input" />
              </Field>
              <Field label="VAT Number" testId="company-vat">
                <Input value={data.company.vat_number} onChange={(e) => setCompany("vat_number", e.target.value)} data-testid="company-vat-input" />
              </Field>
            </div>
            {/* Phase P.26 — structured billing address, required for VAT
                compliance on subscription invoices. Falls back to the
                legacy free-form textarea below for tenants who want to
                carry over multi-line branding text on their invoices. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="company-address-block">
              <Field label="Country (ISO code)" testId="company-country">
                <Input value={data.company.country || ""} onChange={(e) => setCompany("country", e.target.value.toUpperCase().slice(0, 2))} placeholder="IE" maxLength={2} data-testid="company-country-input" />
              </Field>
              <Field label="Postal code" testId="company-postal">
                <Input value={data.company.postal_code || ""} onChange={(e) => setCompany("postal_code", e.target.value)} placeholder="D02 XY45" data-testid="company-postal-input" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Address line 1" testId="company-address-line1">
                <Input value={data.company.address_line1 || ""} onChange={(e) => setCompany("address_line1", e.target.value)} placeholder="24 Main Street" data-testid="company-address-line1-input" />
              </Field>
              <Field label="City" testId="company-city">
                <Input value={data.company.city || ""} onChange={(e) => setCompany("city", e.target.value)} placeholder="Dublin" data-testid="company-city-input" />
              </Field>
            </div>
            <Field label="Address (free-form, appears on invoices)">
              <Textarea rows={2} value={data.company.address} onChange={(e) => setCompany("address", e.target.value)} data-testid="company-address-input" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Phone">
                <Input value={data.company.phone} onChange={(e) => setCompany("phone", e.target.value)} data-testid="company-phone-input" />
              </Field>
              <Field label="Email">
                <Input type="email" value={data.company.email} onChange={(e) => setCompany("email", e.target.value)} data-testid="company-email-input" />
              </Field>
            </div>

            {/* Phase 38 — Contact details for invoices */}
            <div className="border-t border-border pt-3 mt-1 space-y-3" data-testid="settings-company-contact-details">
              <div>
                <h3 className="text-sm font-extrabold uppercase tracking-wider text-muted-foreground">Contact details for invoices</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Shown on invoices if referenced via <code className="bg-secondary px-1 rounded">{`{{token}}`}</code> in your invoice template.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Mobile">
                  <Input value={data.company.mobile || ""} onChange={(e) => setCompany("mobile", e.target.value)} placeholder="+353 87 …" data-testid="company-mobile-input" />
                </Field>
                <Field label="Website">
                  <Input value={data.company.website || ""} onChange={(e) => setCompany("website", e.target.value)} placeholder="rbconcrete.ie" data-testid="company-website-input" />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Company registration number">
                  <Input value={data.company.registration_number || ""} onChange={(e) => setCompany("registration_number", e.target.value)} placeholder="e.g. 654321" data-testid="company-registration-number-input" />
                </Field>
                <Field label="Tagline">
                  <Input value={data.company.tagline || ""} onChange={(e) => setCompany("tagline", e.target.value)} placeholder="One-line strapline shown on invoices" data-testid="company-tagline-input" />
                </Field>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <Label className="text-xs uppercase tracking-wider font-semibold">Logo</Label>
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 border border-border rounded-[6px] grid place-items-center bg-secondary overflow-hidden">
                  {data.company.logo_url ? (
                    <img src={buildAssetUrl(data.company.logo_url)} alt="logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
                  ) : (
                    <span className="text-[10px] text-muted-foreground mono uppercase">No logo</span>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  ref={fileRef}
                  className="hidden"
                  onChange={(e) => onLogo(e.target.files?.[0])}
                  data-testid="logo-file-input"
                />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload-button">
                  <Upload className="h-4 w-4 mr-1" /> {uploading ? "Uploading..." : "Upload"}
                </Button>
              </div>
            </div>

            {/* Phase 44 — brand accent color (tints the external-payment QR
                and any future brandable surface). Pickable via a native
                color input + a free-form hex input for paste-in. */}
            <div className="space-y-2 pt-2" data-testid="settings-brand-accent-block">
              <Label className="text-xs uppercase tracking-wider font-semibold">Brand accent color</Label>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="color"
                  value={(data.company.brand_accent_color || "#0EA5E9").toString()}
                  onChange={(e) => setCompany("brand_accent_color", e.target.value.toUpperCase())}
                  className="h-10 w-12 rounded-[6px] border border-border cursor-pointer bg-transparent p-1"
                  data-testid="brand-accent-color-picker"
                />
                <Input
                  value={data.company.brand_accent_color || ""}
                  onChange={(e) => setCompany("brand_accent_color", e.target.value)}
                  placeholder="#0EA5E9"
                  maxLength={9}
                  className="font-mono text-xs max-w-[140px]"
                  data-testid="brand-accent-color-hex"
                />
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"
                >
                  Used to tint your invoice QR codes. Falls back to black when contrast against white is too low.
                </span>
              </div>
            </div>
          </div>
        </section>
        {/* Phase K — Base / depot address (mileage premium feature). Lives
            inside Company basics so the manager configures one address per
            tenant. Save is handled by its own endpoint because the geocoder
            runs synchronously and we want to surface lat/lng feedback. */}
        <BaseAddressBlock data={data} reload={load} />
        </SettingsSection>

        <SettingsSection slug="invoice-templates" title="Invoice templates" subtitle="Layout, header/footer copy, accent colour, logo position">
        {/* Phase 9: Invoice templates list (multi-template) */}
        <InvoiceTemplatesPane />
        </SettingsSection>

        <SettingsSection slug="stripe-payments" title="Online payments" subtitle="Resend & ORS API keys, sender email">
        {/* Phase P.10 — Stripe Connect card feature-gated (see
            frontend/src/lib/featureFlags.js). Backend endpoints stay
            intact so we can re-enable via a single flag flip. */}
        {CONNECT_ENABLED && <StripeConnectCard disabled={!amOwner} />}

        {/* API keys */}
        <section className="bg-card border border-border rounded-[8px] p-6 space-y-4" data-testid="settings-api-keys">
          <h2 className="text-lg font-extrabold">API keys</h2>
          <p className="text-xs text-muted-foreground">Optional — used by invoice email delivery (Resend) and travel-time calculation (OpenRouteService). Stored masked; leave blank to disable.</p>
          <Field label="Resend API key">
            <MaskedInput value={data.api_keys.resend_api_key} onChange={(v) => setApiKey("resend_api_key", v)} testId="resend-key-input" placeholder="re_..." />
          </Field>
          <Field label="ORS API key">
            <MaskedInput value={data.api_keys.ors_api_key} onChange={(v) => setApiKey("ors_api_key", v)} testId="ors-key-input" placeholder="5b3ce..." />
          </Field>
          <Field label="From email (sender)">
            <Input
              value={data.from_email || ""}
              onChange={(e) => setData((d) => ({ ...d, from_email: e.target.value }))}
              placeholder='Your Co <invoices@yourdomain.com> — leave blank to use platform default'
              data-testid="from-email-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">Falls back to platform default if blank. Domain must be verified in Resend.</p>
          </Field>
        </section>
        </SettingsSection>

        <SettingsSection slug="email-templates" title="Email templates" subtitle="Tenant defaults, deprecation notice, customer-facing copy">
        {/* Phase 31: Email templates are now PLATFORM-WIDE (super-admin only).
            The previous in-line editor is replaced with a deprecation note. */}
        <section
          className="bg-card border border-dashed border-border rounded-[8px] p-6"
          data-testid="settings-email-templates-deprecation"
        >
          <h2 className="text-lg font-extrabold flex items-center gap-2">
            <span>Email notifications</span>
            <span className="text-[10px] mono uppercase tracking-wider bg-secondary text-muted-foreground px-2 py-0.5 rounded-full border border-border">
              Platform-managed
            </span>
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            Email templates (booking confirmations, payment receipts, subscription reminders, etc.)
            are now managed centrally by the JobsBook platform admin team. Reach out at
            <span className="font-mono"> info@jobsbook.app</span> if you need a wording change.
          </p>
        </section>

        {/* Phase 62.2 — Email defaults (editable invoice email template) */}
        <EmailDefaultsPane disabled={!amOwner} />
        </SettingsSection>

        <SettingsSection slug="invoice-settings" title="Invoice settings" subtitle="Payment terms, bank details, external pay, footer preview">
        {/* Phase 34 follow-up — per-tenant Invoice Settings */}
        <section className="bg-card border border-border rounded-[8px] p-6 space-y-5" data-testid="settings-invoice">
          <div>
            <h2 className="text-lg font-extrabold">Invoice Settings</h2>
            <p className="text-[12px] text-muted-foreground mt-1">
              These appear on every PDF invoice your customers receive.
              Leave any field blank to use the JobsBook default (or to omit
              the block entirely for bank details).
            </p>
          </div>

          {/* Payment terms */}
          <div className="space-y-2" data-testid="settings-invoice-payment-terms-block">
            <div className="flex items-center justify-between gap-3">
              <label className="text-sm font-semibold">Payment Terms</label>
              <Button
                variant="ghost"
                size="sm"
                className="text-[11px] mono"
                onClick={() => setData((d) => ({ ...d, invoice_payment_terms_text: "" }))}
                data-testid="settings-invoice-reset-terms"
              >
                Reset to default
              </Button>
            </div>
            <textarea
              className="w-full min-h-[120px] rounded-[6px] border border-border bg-background p-3 text-sm font-mono"
              maxLength={500}
              placeholder={
                "Payment due within 7 days of issue date.\n" +
                "Payments to be made by card or transfer to below account.\n" +
                "Please add {{invoice_number}} as reference."
              }
              value={data.invoice_payment_terms_text ?? ""}
              onChange={(e) =>
                setData((d) => ({ ...d, invoice_payment_terms_text: e.target.value }))
              }
              data-testid="settings-invoice-payment-terms-input"
            />
            <div className="text-[11px] text-muted-foreground">
              {(data.invoice_payment_terms_text ?? "").length}/500 chars · Markdown supported · {"{{invoice_number}}"} renders the invoice number
            </div>
          </div>

          {/* Bank details */}
          <div className="space-y-2 border-t border-border pt-5" data-testid="settings-invoice-bank-block">
            <div>
              <label className="text-sm font-semibold">Bank Details</label>
              <p className="text-[11px] text-muted-foreground mt-1">
                Leave blank to omit from invoices. If any field is set, the bank-details block will render with only the populated fields.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-[11px] mono uppercase tracking-wider text-muted-foreground">Bank name</label>
                <Input
                  value={data.invoice_bank_name ?? ""}
                  onChange={(e) => setData((d) => ({ ...d, invoice_bank_name: e.target.value }))}
                  placeholder="e.g. Bank of Ireland"
                  maxLength={80}
                  data-testid="settings-invoice-bank-name"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] mono uppercase tracking-wider text-muted-foreground">IBAN</label>
                <Input
                  value={data.invoice_bank_iban ?? ""}
                  onChange={(e) => setData((d) => ({ ...d, invoice_bank_iban: e.target.value }))}
                  placeholder="IE12 BOFI 9000 1234 5678"
                  maxLength={50}
                  className="font-mono"
                  data-testid="settings-invoice-bank-iban"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] mono uppercase tracking-wider text-muted-foreground">BIC</label>
                <Input
                  value={data.invoice_bank_bic ?? ""}
                  onChange={(e) => setData((d) => ({ ...d, invoice_bank_bic: e.target.value }))}
                  placeholder="BOFIIE2D"
                  maxLength={20}
                  className="font-mono"
                  data-testid="settings-invoice-bank-bic"
                />
              </div>
            </div>
          </div>

          {/* Phase 62.4 — "How to pay" copy, used by Send Invoice quick-insert button */}
          <div className="space-y-2 border-t border-border pt-5" data-testid="settings-invoice-how-to-pay-block">
            <label className="text-sm font-semibold">How to pay (email quick-insert)</label>
            <p className="text-[11px] text-muted-foreground">
              One-click insert into the email body when sending an invoice.
              Use it for free-form payment instructions you don&apos;t want on the PDF
              (e.g. preferred bank, Revolut tag, reference format). Tokens like{" "}
              <span className="mono">{"{{invoice_number}}"}</span> and{" "}
              <span className="mono">{"{{total}}"}</span> are rendered.
            </p>
            <Textarea
              value={data.payment_instructions ?? ""}
              onChange={(e) => setData((d) => ({ ...d, payment_instructions: e.target.value }))}
              placeholder={
                "Please transfer the total to:\n" +
                "  Bank of Ireland · IE12 BOFI 9000 1234 5678 · BOFIIE2D\n" +
                "Use {{invoice_number}} as the payment reference."
              }
              rows={5}
              maxLength={4000}
              data-testid="settings-payment-instructions-input"
            />
            <div className="text-[11px] text-muted-foreground">
              {(data.payment_instructions ?? "").length}/4000 chars
            </div>
          </div>

          {/* Phase 40 — External payment link (Revolut/PayPal/Wise/SumUp/…) */}
          <div className="space-y-3 border-t border-border pt-5" data-testid="settings-external-payment-block">
            <div>
              <label className="text-sm font-semibold">External payment link (no Stripe required)</label>
              <p className="text-[11px] text-muted-foreground mt-1">
                Embed a Revolut Business / PayPal / Wise payment link on your customer invoices. Customers can scan the QR or click the link. You'll need to mark invoices paid manually when the money lands in your account.
              </p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Switch
                checked={!!data.external_payment_enabled}
                onCheckedChange={(v) => setData((d) => ({ ...d, external_payment_enabled: !!v }))}
                data-testid="settings-external-payment-enable-toggle"
              />
              <span className="text-sm leading-tight">
                <span className="font-semibold">Enable external payment link</span>
                <span className="block text-[11px] text-muted-foreground mt-0.5">
                  Shows the link + QR on every newly-issued invoice PDF and in the customer email.
                </span>
              </span>
            </label>
            {data.external_payment_enabled && (
              <div className="space-y-3 pl-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[11px] mono uppercase tracking-wider text-muted-foreground">Label</label>
                    <Input
                      value={data.external_payment_label ?? ""}
                      onChange={(e) => setData((d) => ({ ...d, external_payment_label: e.target.value }))}
                      placeholder="Pay with Revolut"
                      maxLength={60}
                      data-testid="settings-external-payment-label"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] mono uppercase tracking-wider text-muted-foreground">Payment URL</label>
                    <Input
                      value={data.external_payment_url ?? ""}
                      onChange={(e) => setData((d) => ({ ...d, external_payment_url: e.target.value }))}
                      placeholder="https://revolut.me/yourbusiness"
                      maxLength={500}
                      type="url"
                      className="font-mono text-xs"
                      data-testid="settings-external-payment-url"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <Switch
                    checked={data.external_payment_qr_enabled !== false}
                    onCheckedChange={(v) => setData((d) => ({ ...d, external_payment_qr_enabled: !!v }))}
                    data-testid="settings-external-payment-qr-toggle"
                  />
                  <span className="text-sm">Show QR code on invoice</span>
                </label>
                <div className="space-y-1">
                  <label className="text-[11px] mono uppercase tracking-wider text-muted-foreground">Instructions (optional)</label>
                  <Textarea
                    value={data.external_payment_instructions ?? ""}
                    onChange={(e) => setData((d) => ({ ...d, external_payment_instructions: e.target.value }))}
                    placeholder="Scan with your Revolut/banking app to pay €{{total}}"
                    maxLength={500}
                    rows={2}
                    data-testid="settings-external-payment-instructions"
                  />
                  <div className="text-[11px] text-muted-foreground">
                    {(data.external_payment_instructions ?? "").length}/500 chars · Shown under the QR code
                  </div>
                </div>

                {/* Phase 44 — Branded QR live preview. Pulls from the
                    server so we render exactly the same PNG that the
                    invoice PDF will embed. Save Settings first to refresh
                    after editing the accent color / logo. */}
                <QrPreviewBlock
                  brandAccentColor={data.company?.brand_accent_color || ""}
                  hasLogo={!!data.company?.logo_url}
                />
              </div>
            )}
          </div>
          <div className="border-t border-border pt-5" data-testid="settings-invoice-footer-preview">
            <label className="text-sm font-semibold">Invoice Footer</label>
            <p className="text-[11px] text-muted-foreground mt-1 mb-3">
              Appears on every page of every invoice. This is fixed and not editable.
            </p>
            <div className="bg-secondary/40 border border-border rounded-[6px] p-4 text-center">
              <div className="text-[11px] mono text-muted-foreground border-t border-border pt-3 mt-1">
                Powered by Your Data App LTD — manage your bookings smarter at{" "}
                <a
                  href="https://jobsbook.app"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline hover:text-accent"
                >
                  jobsbook.app
                </a>
              </div>
            </div>
          </div>
        </section>
        </SettingsSection>

        <SettingsSection slug="backups" title="Backups & accounts export" subtitle="Daily backup, notifications, tax, currency & counters">
        {/* Phase 63.1 — Daily backup configuration */}
        <DailyBackupPane disabled={!amOwner} />

        {/* Phase 27 — Pay-online button toggle on customer invoice emails */}
        {/* Phase 34 — Automatic payment-reminder email tiers */}
        <section className="bg-card border border-border rounded-[8px] p-6 space-y-5" data-testid="settings-notifications">
          <h2 className="text-lg font-extrabold">Notifications</h2>

          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="font-semibold">Show "Pay Online" button in customer invoice emails</div>
              <p className="text-[12px] text-muted-foreground mt-1">
                When enabled, every invoice email includes a Pay Online CTA that links to a one-click Stripe Checkout page for the invoice's total. Disable to send invoice PDFs only.
              </p>
            </div>
            <Switch
              checked={!!data.pay_online_button_enabled}
              onCheckedChange={(v) => setData((d) => ({ ...d, pay_online_button_enabled: !!v }))}
              data-testid="settings-pay-online-toggle"
            />
          </div>

          <div className="border-t border-border pt-5">
            <div className="font-semibold">Automatic payment reminders</div>
            <p className="text-[12px] text-muted-foreground mt-1">
              Reminder emails are sent automatically to customers with unpaid
              invoices at 3, 7, and 14 days overdue. Each tier is sent at most
              once per invoice. Customise the message in{" "}
              <span className="mono">/admin/email-templates</span> (super-admin).
            </p>
            <div className="mt-4 space-y-3">
              {[
                { key: "reminder_tier_3d_enabled",  label: "3 days overdue",  tier: 3,  code: "invoice_reminder_t3" },
                { key: "reminder_tier_7d_enabled",  label: "7 days overdue",  tier: 7,  code: "invoice_reminder_t7" },
                { key: "reminder_tier_14d_enabled", label: "14 days overdue (final)", tier: 14, code: "invoice_reminder_t14" },
              ].map((t) => (
                <div
                  key={t.key}
                  className="flex items-center justify-between gap-4 bg-secondary/40 border border-border rounded-[6px] px-4 py-3"
                  data-testid={`settings-reminder-tier-row-${t.tier}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={
                        "h-9 w-9 rounded-full flex items-center justify-center font-black tabular-nums text-sm " +
                        (t.tier === 3
                          ? "bg-amber-100 text-amber-700"
                          : t.tier === 7
                          ? "bg-orange-100 text-orange-700"
                          : "bg-red-100 text-red-700")
                      }
                    >
                      {t.tier}d
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{t.label}</div>
                      <div className="text-[11px] mono text-muted-foreground mt-0.5">{t.code}</div>
                    </div>
                  </div>
                  <Switch
                    checked={data[t.key] !== false}
                    onCheckedChange={(v) => setData((d) => ({ ...d, [t.key]: !!v }))}
                    data-testid={`settings-reminder-tier-${t.tier}-toggle`}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tax + counters + reset */}
        <section className="bg-card border border-border rounded-[8px] p-6 space-y-4" data-testid="settings-tax">
          <h2 className="text-lg font-extrabold">Tax, currency & counters</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Default VAT rate (%)">
              <Input type="number" step="0.01" value={data.default_vat_rate} onChange={(e) => setData((d) => ({ ...d, default_vat_rate: e.target.value }))} data-testid="vat-rate-input" />
            </Field>
            <Field label="Currency">
              <Select value={data.currency || "EUR"} onValueChange={(v) => setData((d) => ({ ...d, currency: v }))}>
                <SelectTrigger data-testid="currency-select">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <ReadOnly label="Booking counter" value={`B-${String(data.booking_counter).padStart(6, "0")}`} testId="booking-counter" />
            <ReadOnly label="Invoice counter" value={`I-${String(data.invoice_counter).padStart(6, "0")}`} testId="invoice-counter" />
          </div>

          {/* Phase 48 — soft conflict warning toggle. */}
          <div className="border-t border-border pt-4 mt-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Label className="text-xs uppercase tracking-wider font-semibold">Warn on time conflicts</Label>
                <p className="text-[12px] text-muted-foreground mt-0.5">
                  Show a warning when a new booking overlaps an existing staff/customer slot or is too far to reach in time.
                </p>
                <p className="text-[11px] text-muted-foreground/80 mt-1 italic" data-testid="warn-on-time-conflicts-helper">
                  When off, bookings save immediately without conflict checks.
                </p>
              </div>
              <Switch
                checked={data.warn_on_time_conflicts !== false}
                onCheckedChange={(v) => setData((d) => ({ ...d, warn_on_time_conflicts: !!v }))}
                data-testid="settings-warn-on-time-conflicts"
              />
            </div>
          </div>

          {/* Phase 53 — Calendar heat-map thresholds (manager-configurable). */}
          <HeatmapThresholdsBlock
            greenMax={Number(data.month_heatmap_green_max ?? 3)}
            amberMax={Number(data.month_heatmap_amber_max ?? 5)}
            onChange={(g, a) => setData((d) => ({
              ...d,
              month_heatmap_green_max: g,
              month_heatmap_amber_max: a,
            }))}
          />

          <div className="border-t border-border pt-4 mt-4">
            <h3 className="text-sm font-extrabold uppercase tracking-widest text-muted-foreground">Demo reset</h3>
            <p
              className="text-xs text-muted-foreground mt-2 max-w-xl"
              data-testid="reset-demo-removed-note"
            >
              Demo reset has been removed. Contact support if you need a fresh sandbox.
            </p>
          </div>
        </section>
        </SettingsSection>

        <SettingsSection slug="job-templates-pricing" title="Job templates &amp; pricing" subtitle="Payment types, paid methods, customer tags">
        <PaymentTypesSection />
        <PaidMethodsSection />
        <CustomerTagsSection />
        </SettingsSection>

        {/* Phase P.15 — Products moved to a dedicated /products page. The
            template proposals review still lives here for tenant admins. */}
        <SettingsSection slug="template-proposals" title="Template proposals" subtitle="Review new templates suggested by staff">
          <TemplateProposalsSection />
        </SettingsSection>

        <SettingsSection slug="staff-pins" title="Staff &amp; PINs" subtitle="Staff job card display, location requirement, audit mode">
        <StaffCardFieldsSection
          value={data.staff_card_fields || ["time", "customer_name", "address", "payment_form"]}
          onChange={(v) => setData((d) => ({ ...d, staff_card_fields: v }))}
          amOwner={amOwner}
        />
        <RequireLocationSection data={data} onChange={(v) => setData((d) => ({ ...d, require_location: v }))} />
        <StaffPromptsSection
          data={data}
          onChange={(next) => setData(next)}
          amOwner={amOwner}
        />
        <AuditModeSection data={data} onChange={(v) => setData((d) => ({ ...d, audit_mode: v }))} amOwner={amOwner} />
        </SettingsSection>

        <SettingsSection slug="calendar-display" title="Calendar display" subtitle="Pick which 1–5 fields appear on every booking card on the calendar">
          <CalendarDisplaySection
            value={data.calendar_display?.fields}
            onChange={(fields) => setData((d) => ({ ...d, calendar_display: { ...(d.calendar_display || {}), fields } }))}
            disabled={!amOwner}
          />
        </SettingsSection>

        {/* Phase P.18 — GDPR "Privacy & data" section. Owner-only for
            account-delete; every manager can trigger a portability export. */}
        <SettingsSection slug="privacy-data" title="Privacy & data" subtitle="Export or delete your tenant's data (GDPR Articles 17, 20)">
          <PrivacyDataSection amOwner={amOwner} />
        </SettingsSection>

        {/* Phase P.7.1 follow-up — removed "Refresh ALL invoices"
            SettingsSection. Every invoice /pdf-stream + /pdf read now
            force-renders with the P.7 live overlay, so the bulk button
            is redundant. Backend `/invoices/bulk-rebuild-from-live`
            endpoint kept for admin debugging (see Phase A.1). */}
      </div>
    </div>
  );
}

// Phase P.18 — GDPR "Privacy & data" section. Two primary actions:
//  (a) Export my data — POST /api/gdpr/export returns a ZIP as an attachment.
//      We fetch as blob and trigger a client-side download.
//  (b) Delete my account — 2-step: warn, then password + confirm. Sets a
//      30-day soft-delete flag; the backend sweep hard-deletes after.
// The card also shows any pending deletion so the owner can cancel it.
function PrivacyDataSection({ amOwner }) {
  const [pending, setPending] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const loadPending = useCallback(async () => {
    try {
      const { data } = await api.get("/gdpr/deletion-status");
      setPending(data?.pending || null);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadPending(); }, [loadPending]);

  const exportData = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await api.post("/gdpr/export", {}, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jobsbook-export-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Data export downloaded");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const submitDelete = async () => {
    if (busy) return;
    if (!password) { toast.error("Password is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/gdpr/delete-account", {
        password, reason, confirm: true,
      });
      toast.success(`Deletion scheduled for ${(data?.scheduled_for || "").slice(0, 10)}`);
      setPassword(""); setReason(""); setStep(1); setConfirmOpen(false);
      loadPending();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const cancelPending = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/gdpr/cancel-deletion");
      toast.success("Deletion cancelled");
      loadPending();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="privacy-data-section">
      <p className="text-xs text-muted-foreground leading-relaxed">
        You can export a machine-readable copy of your tenant's data (Art. 20 GDPR portability)
        or schedule permanent deletion (Art. 17 erasure). Invoicing records are anonymised on
        deletion but retained for the 7-year Irish Revenue retention period.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border border-border rounded-md p-4 space-y-2">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Portability</div>
          <div className="font-semibold text-sm">Export my data</div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Downloads a ZIP with JSON files: managers, customers, bookings, invoices, ledgers,
            templates, staff, products, chat messages, status events and consent audit.
          </p>
          <Button
            onClick={exportData}
            disabled={exporting}
            className="btn-cta w-full"
            data-testid="gdpr-export-btn"
          >
            {exporting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Preparing…</> : "Export my data (ZIP)"}
          </Button>
        </div>

        <div className={`border rounded-md p-4 space-y-2 ${pending ? "border-amber-300 bg-amber-50" : "border-rose-200 bg-rose-50/40"}`}>
          <div className="mono text-[10px] uppercase tracking-widest text-rose-700">Erasure</div>
          <div className="font-semibold text-sm">Delete my account</div>
          {pending ? (
            <div className="space-y-2" data-testid="gdpr-pending-block">
              <p className="text-xs text-amber-900 leading-relaxed">
                Deletion is scheduled for{" "}
                <span className="mono font-bold">{(pending.scheduled_for || "").slice(0, 10)}</span>.
                All data will be permanently removed on that date, except invoicing records
                which will be anonymised and retained for 7 years.
              </p>
              <Button
                variant="outline"
                onClick={cancelPending}
                disabled={busy}
                className="w-full"
                data-testid="gdpr-cancel-deletion-btn"
              >
                Cancel scheduled deletion
              </Button>
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Schedules permanent deletion of your tenant in 30 days. Owner-only, requires
                password re-entry.
              </p>
              <Button
                variant="outline"
                onClick={() => { setStep(1); setConfirmOpen(true); }}
                disabled={!amOwner}
                className="w-full text-rose-700 border-rose-300 hover:bg-rose-100"
                data-testid="gdpr-delete-account-btn"
              >
                Delete my account…
              </Button>
              {!amOwner && (
                <p className="text-[11px] text-rose-800/80 italic">Only the tenant owner can delete the account.</p>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!o) { setConfirmOpen(false); setStep(1); } }}>
        <DialogContent data-testid="gdpr-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete my account</DialogTitle>
            <DialogDescription>
              This is a two-step, irreversible operation. Take a moment before continuing.
            </DialogDescription>
          </DialogHeader>
          {step === 1 ? (
            <div className="space-y-3 text-sm" data-testid="gdpr-delete-step-1">
              <div className="border border-rose-300 bg-rose-50 rounded-md p-3 text-rose-900">
                <div className="font-bold mb-1">You are about to schedule permanent deletion</div>
                <ul className="list-disc pl-5 space-y-1 text-[13px]">
                  <li>All customers, bookings, staff, templates, ledgers, chat messages and photos will be permanently deleted after 30 days.</li>
                  <li>Invoices &amp; ledger tax records will be anonymised (personal identifiers stripped) but retained for 7 years to comply with Irish Revenue.</li>
                  <li>You can cancel the scheduled deletion at any time within the 30-day grace window.</li>
                  <li>Active subscriptions must be cancelled separately in the Stripe Customer Portal.</li>
                </ul>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="gdpr-delete-cancel-1">Cancel</Button>
                <Button
                  onClick={() => setStep(2)}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="gdpr-delete-continue"
                >
                  I understand — continue
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3" data-testid="gdpr-delete-step-2">
              <div>
                <Label>Confirm with your password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  data-testid="gdpr-delete-password"
                />
              </div>
              <div>
                <Label>Reason (optional)</Label>
                <Textarea
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Helps us improve. Not required."
                  data-testid="gdpr-delete-reason"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStep(1)} disabled={busy}>Back</Button>
                <Button
                  onClick={submitDelete}
                  disabled={busy || !password}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="gdpr-delete-confirm"
                >
                  {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Scheduling…</> : "Schedule deletion in 30 days"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Phase 53 — manager-configurable colour thresholds for the month-view
// day-count heat-map pill. Editing the two number inputs live-updates the
// helper text and a 6-pill sample preview row so the manager can see the
// effect before pressing Save.
function heatmapPillClass(count, greenMax, amberMax) {
  if (count > amberMax) return "bg-red-100 text-red-800";
  if (count > greenMax) return "bg-amber-100 text-amber-800";
  return "bg-emerald-100 text-emerald-800";
}

function HeatmapThresholdsBlock({ greenMax, amberMax, onChange }) {
  const g = Number.isFinite(greenMax) ? greenMax : 3;
  const a = Number.isFinite(amberMax) ? amberMax : 5;
  const gInvalid = !(g >= 1 && g <= 50);
  const aInvalid = !(a >= 1 && a <= 50);
  const orderInvalid = !(g < a);
  const sampleCounts = [1, 3, 4, 5, 6, 10];

  const inputCls = (bad) =>
    `${bad ? "border-red-500 ring-2 ring-red-200 focus-visible:ring-red-300" : ""}`.trim();

  return (
    <div
      className="border-t border-border pt-4 mt-4 space-y-3"
      data-testid="settings-heatmap-thresholds"
    >
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">Calendar heat-map thresholds</Label>
        <p className="text-[12px] text-muted-foreground mt-0.5">
          Controls the colour of the day-count pill on the month-view calendar.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Green up to (jobs/day)">
          <Input
            type="number"
            min={1}
            max={50}
            step={1}
            value={String(g)}
            onChange={(e) => {
              const v = e.target.value === "" ? 0 : Number(e.target.value);
              onChange(Number.isFinite(v) ? v : g, a);
            }}
            className={inputCls(gInvalid || orderInvalid)}
            data-testid="heatmap-green-max-input"
            aria-invalid={gInvalid || orderInvalid}
          />
        </Field>
        <Field label="Amber up to (jobs/day)">
          <Input
            type="number"
            min={1}
            max={50}
            step={1}
            value={String(a)}
            onChange={(e) => {
              const v = e.target.value === "" ? 0 : Number(e.target.value);
              onChange(g, Number.isFinite(v) ? v : a);
            }}
            className={inputCls(aInvalid || orderInvalid)}
            data-testid="heatmap-amber-max-input"
            aria-invalid={aInvalid || orderInvalid}
          />
        </Field>
      </div>
      <p
        className="text-[12px] text-muted-foreground"
        data-testid="heatmap-threshold-helper"
      >
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
          Green: 1–{g} jobs
        </span>
        <span className="mx-1.5 text-muted-foreground/50">·</span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
          Amber: {g + 1}–{a} jobs
        </span>
        <span className="mx-1.5 text-muted-foreground/50">·</span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-red-500" aria-hidden="true" />
          Red: {a + 1}+ jobs
        </span>
      </p>
      {(gInvalid || aInvalid || orderInvalid) && (
        <p
          className="text-[12px] text-red-600 font-medium"
          data-testid="heatmap-threshold-error"
        >
          {orderInvalid
            ? "Green must be strictly less than Amber."
            : "Each threshold must be between 1 and 50."}
        </p>
      )}
      <div>
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
          Live preview
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="heatmap-threshold-preview">
          {sampleCounts.map((c) => (
            <span
              key={c}
              className={`inline-flex items-center mono text-[11px] uppercase font-bold rounded-full px-2 py-0.5 whitespace-nowrap ${heatmapPillClass(c, g, a)}`}
              data-testid={`heatmap-threshold-preview-${c}`}
            >
              {c}&nbsp;job{c === 1 ? "" : "s"}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function StaffCardFieldsSection({ value, onChange, amOwner }) {
  const STAFF_CARD_OPTIONS = [
    { key: "time", label: "Time" },
    { key: "customer_name", label: "Customer name" },
    { key: "address", label: "Address" },
    { key: "payment_form", label: "Payment form", note: "Always shown as a coloured badge on staff cards — this row is redundant but kept for backward compatibility." },
    { key: "job_template", label: "Job template" },
    { key: "duration", label: "Duration" },
    { key: "phone", label: "Customer phone" },
    { key: "postcode", label: "Postcode" },
    { key: "notes_preview", label: "Notes preview" },
  ];
  const [busy, setBusy] = useState(false);
  const selected = Array.isArray(value) ? value : [];
  const orderOf = (k) => selected.indexOf(k); // -1 if unselected

  const toggleField = (k) => {
    if (orderOf(k) >= 0) {
      onChange(selected.filter((x) => x !== k));
    } else {
      if (selected.length >= 4) return; // cap, silent — disabled state in UI
      onChange([...selected, k]);
    }
  };

  const save = async () => {
    if (selected.length === 0) {
      toast.error("Select at least one field.");
      return;
    }
    if (selected.length > 4) {
      toast.error("Select no more than 4 fields.");
      return;
    }
    setBusy(true);
    try {
      const { data: d } = await api.put("/settings", { staff_card_fields: selected });
      onChange(d.staff_card_fields || selected);
      window.dispatchEvent(new Event("jobsbook:settings-changed"));
      toast.success("Staff job card fields updated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="bg-card border border-border rounded-[8px] p-6 mt-6"
      data-testid="settings-staff-card-fields"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold">Staff Job Card Display</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Choose up to 4 fields shown on each staff job card (top to bottom in the order you pick).
          </p>
        </div>
        <Button
          onClick={save}
          disabled={busy || !amOwner}
          data-testid="settings-staff-card-save"
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {STAFF_CARD_OPTIONS.map((opt) => {
          const order = orderOf(opt.key);
          const isSelected = order >= 0;
          const isDisabled = !amOwner || (!isSelected && selected.length >= 4);
          return (
            <label
              key={opt.key}
              data-testid={`settings-staff-card-option-${opt.key}`}
              className={`flex items-center gap-3 border rounded-md px-3 py-2 cursor-pointer transition-colors ${
                isSelected
                  ? "border-accent bg-accent/5"
                  : "border-border hover:border-muted-foreground"
              } ${isDisabled && !isSelected ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <input
                type="checkbox"
                className="h-4 w-4 accent-accent"
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => toggleField(opt.key)}
                data-testid={`settings-staff-card-checkbox-${opt.key}`}
              />
              <span className="flex-1 text-sm">
                {opt.label}
                {opt.note && (
                  <span
                    className="block text-[11px] text-muted-foreground font-normal mt-0.5 leading-snug"
                    data-testid={`settings-staff-card-note-${opt.key}`}
                  >
                    {opt.note}
                  </span>
                )}
              </span>
              {isSelected && (
                <span
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-accent text-accent-foreground text-[11px] font-extrabold mono"
                  data-testid={`settings-staff-card-order-${opt.key}`}
                >
                  {order + 1}
                </span>
              )}
            </label>
          );
        })}
      </div>

      <div className="mt-3 text-[11px] text-muted-foreground mono">
        {selected.length}/4 selected
      </div>
    </section>
  );
}


function RequireLocationSection({ data, onChange }) {
  const [busy, setBusy] = useState(false);
  const toggle = async (v) => {
    setBusy(true);
    try {
      await api.put("/settings", { require_location: v });
      onChange(v);
      window.dispatchEvent(new Event("jobsbook:settings-changed"));
      toast.success(v ? "Location required on bookings" : "Location hidden on bookings");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <section className="bg-card border border-border rounded-[8px] p-6 mt-6" data-testid="settings-require-location">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold">Require location on bookings</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            On by default. Turn off if your business operates from a fixed location (salon, workshop, office) and you don't need per-booking address fields. Travel-time calculations are skipped while this is off.
          </p>
        </div>
        <Switch
          checked={data.require_location !== false}
          onCheckedChange={toggle}
          disabled={busy}
          data-testid="settings-require-location-toggle"
        />
      </div>
    </section>
  );
}

// Phase P.11 — Toggle for the "Send invoice to customer now?" popup that
// fires after a staff member marks a VAT/RCT booking as completed. Cash
// payment types are unaffected regardless of this setting.
function StaffPromptsSection({ data, onChange, amOwner }) {
  const [busy, setBusy] = useState(false);
  const initial = !!((data.staff_prompts || {}).send_invoice_on_complete ?? true);
  const [enabled, setEnabled] = useState(initial);
  useEffect(() => { setEnabled(initial); }, [initial]);

  const toggle = async (v) => {
    if (!amOwner) return;
    setBusy(true);
    setEnabled(v);
    try {
      const { data: d } = await api.put("/settings", {
        staff_prompts: { send_invoice_on_complete: v },
      });
      onChange(d);
      window.dispatchEvent(new Event("jobsbook:settings-changed"));
      toast.success(v ? "Staff will be prompted to send invoices after completion" : "Staff will not be prompted");
    } catch (e) {
      setEnabled(!v);
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-card border border-border rounded-[8px] p-6 mt-6" data-testid="settings-staff-prompts">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold">Prompt staff to send invoice after completion</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            When ON, staff who mark a VAT or RCT job as completed see a "Send invoice now?" popup. Cash payment types are never prompted — those stay manager-driven.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={toggle}
          disabled={busy || !amOwner}
          data-testid="settings-staff-prompt-toggle"
        />
      </div>
    </section>
  );
}


function AuditModeSection({ data, onChange, amOwner }) {
  const [busy, setBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwValue, setPwValue] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const pendingRef = useRef(null); // holds the target value while modal is open

  const current = !!data.audit_mode;

  const requestToggle = (v) => {
    if (!amOwner || busy) return;
    pendingRef.current = v;
    setPwValue("");
    setPwOpen(true);
  };

  const applyToggle = async (v) => {
    setBusy(true);
    try {
      await api.post("/settings/cash-filter", { enabled: v });
      onChange(v);
      window.dispatchEvent(new Event("jobsbook:settings-changed"));
      toast.success(v ? "Smart Move enabled" : "Smart Move disabled");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmPassword = async () => {
    if (!pwValue) { toast.error("Enter your password"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/verify-password", { password: pwValue });
      const target = pendingRef.current;
      setPwOpen(false);
      setPwValue("");
      pendingRef.current = null;
      await applyToggle(target);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) toast.error("Incorrect password");
      else toast.error(formatApiError(e?.response?.data?.detail) || "Verification failed");
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <>
      <section
        className="bg-card border border-border rounded-[6px] px-4 py-2.5 mt-4 flex items-center justify-between gap-3"
        data-testid="settings-cash-filter"
      >
        <span className="text-sm font-semibold text-foreground">Smart Move</span>
        <Switch
          checked={current}
          onCheckedChange={requestToggle}
          disabled={busy || !amOwner}
          data-testid="settings-cash-filter-toggle"
        />
      </section>

      <Dialog open={pwOpen} onOpenChange={(o) => { if (!pwBusy) { setPwOpen(o); if (!o) { pendingRef.current = null; setPwValue(""); } } }}>
        <DialogContent data-testid="smart-move-password-dialog">
          <DialogHeader>
            <DialogTitle>Confirm change</DialogTitle>
            <DialogDescription>
              Re-enter your password to change this setting.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="smart-move-pw">Password</Label>
            <Input
              id="smart-move-pw"
              type="password"
              autoFocus
              value={pwValue}
              onChange={(e) => setPwValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !pwBusy) confirmPassword(); }}
              disabled={pwBusy}
              data-testid="smart-move-password-input"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { setPwOpen(false); setPwValue(""); pendingRef.current = null; }}
              disabled={pwBusy}
              data-testid="smart-move-password-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmPassword}
              disabled={pwBusy || !pwValue}
              data-testid="smart-move-password-confirm"
            >
              {pwBusy ? "Verifying…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PaidMethodsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/paid-methods", { params: { include_archived: true } });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const archive = async (id, archived) => {
    try {
      if (archived) {
        await api.put(`/settings/paid-methods/${id}`, { archived: false });
      } else {
        await api.delete(`/settings/paid-methods/${id}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-extrabold">Paid methods</h2>
        <Button size="sm" className="btn-cta" onClick={() => setCreating(true)} data-testid="add-paid-method-button">
          <Plus className="h-3 w-3 mr-1" /> New method
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-1 mb-3">
        Where cash is deposited when an invoice is marked paid. Managers must pick one each time.
      </p>
      {loading ? <div className="text-muted-foreground text-sm">Loading…</div> : (
        <div className="bg-card border border-border rounded-[8px] divide-y divide-border" data-testid="paid-methods-list">
          {items.map((p) => (
            <div key={p.id} className={`p-3 flex items-center gap-3 ${p.archived ? "opacity-60" : ""}`} data-testid={`paid-method-row-${p.code}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm flex items-center gap-2 flex-wrap">
                  {p.name}
                  <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{p.code}</span>
                  {p.user_name ? (
                    <span className="mono text-[10px] uppercase tracking-widest bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5 rounded" data-testid={`pm-linked-user-${p.code}`} title={`Linked to ${p.user_role}`}>
                      → {p.user_name}
                    </span>
                  ) : (
                    <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground italic" data-testid={`pm-unassigned-${p.code}`}>
                      → unassigned
                    </span>
                  )}
                  {p.archived && <span className="mono text-[10px] uppercase tracking-widest bg-muted px-1.5 py-0.5 rounded">Archived</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`pm-edit-${p.code}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => archive(p.id, p.archived)} className={p.archived ? "" : "text-destructive hover:text-destructive"} data-testid={`pm-archive-${p.code}`}>
                  {p.archived ? "Restore" : "Archive"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {(editing || creating) && (
        <PaidMethodDialog
          initial={editing}
          isCreate={creating}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function PaidMethodDialog({ initial, isCreate, onClose, onSaved }) {
  const [form, setForm] = useState(initial || { name: "", code: "", user_id: "" });
  const [submitting, setSubmitting] = useState(false);
  // Phase F — load eligible collectors for the "Linked user" dropdown.
  const [eligible, setEligible] = useState([]);
  useEffect(() => {
    api.get("/users/eligible-collectors").then((r) => {
      setEligible(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setEligible([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        // Phase F — explicit empty string clears the link; null/undefined
        // means "do not change" on PUT.
        user_id: (form.user_id ?? "") || "",
      };
      if (isCreate && form.code?.trim()) payload.code = form.code.trim();
      if (isCreate) {
        await api.post("/settings/paid-methods", payload);
        toast.success("Paid method created");
      } else {
        await api.put(`/settings/paid-methods/${initial.id}`, payload);
        toast.success("Paid method updated");
      }
      // Phase E — broadcast so Mark-Paid listeners refetch.
      try { window.dispatchEvent(new Event("jobsbook:paid-methods-changed")); } catch { /* noop */ }
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2?.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="paid-method-dialog">
        <DialogHeader>
          <DialogTitle>{isCreate ? "New paid method" : `Edit ${initial?.name}`}</DialogTitle>
          <DialogDescription>{isCreate ? "Code auto-generates from name if blank." : "Code is immutable after creation."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="pm-name-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold">Code</Label>
              <Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!isCreate} data-testid="pm-code-input" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider font-semibold">Linked user (optional)</Label>
            <Select
              value={form.user_id || "__unset__"}
              onValueChange={(v) => setForm({ ...form, user_id: v === "__unset__" ? "" : v })}
            >
              <SelectTrigger data-testid="paid-method-user-select">
                <SelectValue placeholder="— unassigned —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unset__" data-testid="paid-method-user-unset">— unassigned —</SelectItem>
                {eligible.map((u) => {
                  const roleStyles = {
                    owner: "bg-amber-50 text-amber-800 border border-amber-300",
                    manager: "bg-sky-50 text-sky-800 border border-sky-300",
                    staff: "bg-muted text-muted-foreground border border-border",
                  };
                  const cls = roleStyles[u.role] || roleStyles.staff;
                  return (
                    <SelectItem key={u.id} value={u.id} data-testid={`paid-method-user-${u.id}`}>
                      <span className="inline-flex items-center gap-2">
                        <span>{u.name}</span>
                        <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${cls}`}>{u.role}</span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              If set, jobs paid through this method are routed to this person&apos;s &quot;My Cash&quot; view.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="btn-cta" data-testid="pm-save-button">
              {submitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const TAG_PALETTE = ["#ef4444", "#f59e0b", "#10b981", "#0ea5e9", "#6366f1", "#8b5cf6", "#ec4899", "#64748b"];

function CustomerTagsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customer-tags", { params: { include_archived: true } });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reorder = async (id, delta) => {
    const idx = items.findIndex((p) => p.id === id);
    if (idx < 0) return;
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= items.length) return;
    const a = items[idx], b = items[newIdx];
    try {
      await Promise.all([
        api.put(`/customer-tags/${a.id}`, { sort_order: newIdx }),
        api.put(`/customer-tags/${b.id}`, { sort_order: idx }),
      ]);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const archive = async (id, archived) => {
    try {
      if (archived) {
        await api.put(`/customer-tags/${id}`, { archived: false });
      } else {
        await api.delete(`/customer-tags/${id}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-extrabold">Customer tags</h2>
        <Button size="sm" className="btn-cta" onClick={() => setCreating(true)} data-testid="add-customer-tag-button">
          <Plus className="h-3 w-3 mr-1" /> New tag
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-1 mb-3">
        Colored labels managers can attach to customers. Show up on customer cards,
        booking forms, and as a dot on calendar cards.
      </p>
      {loading ? <div className="text-muted-foreground text-sm">Loading…</div> : (
        <div className="bg-card border border-border rounded-[8px] divide-y divide-border" data-testid="customer-tags-list">
          {items.map((t, i) => (
            <div key={t.id} className={`p-3 flex items-center gap-3 ${t.archived ? "opacity-60" : ""}`} data-testid={`customer-tag-row-${t.id}`}>
              <div className="flex flex-col">
                <button type="button" disabled={i === 0} onClick={() => reorder(t.id, -1)} className="text-muted-foreground hover:text-foreground disabled:opacity-30 text-xs" data-testid={`tag-up-${t.id}`}>▲</button>
                <button type="button" disabled={i === items.length - 1} onClick={() => reorder(t.id, 1)} className="text-muted-foreground hover:text-foreground disabled:opacity-30 text-xs" data-testid={`tag-down-${t.id}`}>▼</button>
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
                <TagChip tag={t} />
                <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.color}</span>
                {t.archived && <span className="mono text-[10px] uppercase tracking-widest bg-muted px-1.5 py-0.5 rounded">Archived</span>}
              </div>
              <div className="flex flex-wrap gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => setEditing(t)} data-testid={`tag-edit-${t.id}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => archive(t.id, t.archived)} className={t.archived ? "" : "text-destructive hover:text-destructive"} data-testid={`tag-archive-${t.id}`}>
                  {t.archived ? "Restore" : "Archive"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {(editing || creating) && (
        <CustomerTagDialog
          initial={editing}
          isCreate={creating}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function CustomerTagDialog({ initial, isCreate, onClose, onSaved }) {
  const [form, setForm] = useState(initial || { name: "", color: TAG_PALETTE[0] });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      const payload = { name: form.name.trim(), color: form.color };
      if (isCreate) {
        await api.post("/customer-tags", payload);
        toast.success("Tag created");
      } else {
        await api.put(`/customer-tags/${initial.id}`, payload);
        toast.success("Tag updated");
      }
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2?.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="customer-tag-dialog">
        <DialogHeader>
          <DialogTitle>{isCreate ? "New customer tag" : `Edit ${initial?.name}`}</DialogTitle>
          <DialogDescription>Pick a short label and a color.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider font-semibold">Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="tag-name-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider font-semibold">Color</Label>
            <div className="flex items-center gap-2 flex-wrap">
              {TAG_PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, color: c })}
                  style={{ backgroundColor: c }}
                  className={`h-8 w-8 rounded-full border-2 transition-all ${form.color === c ? "ring-2 ring-offset-2 ring-accent scale-110" : "border-border"}`}
                  data-testid={`tag-color-${c}`}
                />
              ))}
              <Input
                type="text"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                placeholder="#hex"
                className="w-28 mono"
                data-testid="tag-color-input"
              />
            </div>
          </div>
          <div className="pt-2">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Preview</div>
            <TagChip tag={{ name: form.name || "Example", color: form.color }} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="btn-cta" data-testid="tag-save-button">
              {submitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PaymentTypesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/payment-types", { params: { include_archived: true } });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reorder = async (id, delta) => {
    const idx = items.findIndex((p) => p.id === id);
    if (idx < 0) return;
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= items.length) return;
    const a = items[idx], b = items[newIdx];
    try {
      await Promise.all([
        api.put(`/settings/payment-types/${a.id}`, { sort_order: newIdx }),
        api.put(`/settings/payment-types/${b.id}`, { sort_order: idx }),
      ]);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const setDefault = async (id) => {
    try {
      await api.put(`/settings/payment-types/${id}`, { is_default: true });
      toast.success("Default updated");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const archive = async (id, archived) => {
    try {
      if (archived) {
        await api.put(`/settings/payment-types/${id}`, { archived: false });
      } else {
        await api.delete(`/settings/payment-types/${id}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-extrabold">Payment types</h2>
        <Button size="sm" className="btn-cta" onClick={() => setCreating(true)} data-testid="add-payment-type-button">
          <Plus className="h-3 w-3 mr-1" /> New type
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-1 mb-3">
        Bookings reference these by <code>code</code> — once created, the code is immutable. Soft-delete (archive) preserves history.
      </p>
      {loading ? <div className="text-muted-foreground text-sm">Loading…</div> : (
        <div className="bg-card border border-border rounded-[8px] divide-y divide-border" data-testid="payment-types-list">
          {items.map((p, i) => (
            <div key={p.id} className={`p-3 flex items-center gap-3 ${p.archived ? "opacity-60" : ""}`} data-testid={`payment-type-row-${p.code}`}>
              <div className="flex flex-col">
                <button type="button" disabled={i === 0} onClick={() => reorder(p.id, -1)} className="text-muted-foreground hover:text-foreground disabled:opacity-30 text-xs" data-testid={`pt-up-${p.code}`}>▲</button>
                <button type="button" disabled={i === items.length - 1} onClick={() => reorder(p.id, 1)} className="text-muted-foreground hover:text-foreground disabled:opacity-30 text-xs" data-testid={`pt-down-${p.code}`}>▼</button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm flex items-center gap-2 flex-wrap">
                  {p.name}
                  <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{p.code}</span>
                  {p.is_default && <span className="mono text-[10px] uppercase tracking-widest bg-accent/15 text-accent border border-accent/30 px-1.5 py-0.5 rounded">Default</span>}
                  {p.off_the_books && <span className="mono text-[10px] uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded" data-testid={`pt-off-the-books-badge-${p.code}`}>Off the books</span>}
                  {p.archived && <span className="mono text-[10px] uppercase tracking-widest bg-muted px-1.5 py-0.5 rounded">Archived</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 flex gap-3 flex-wrap">
                  <span>Tax {Number(p.tax_rate).toFixed(2)}%</span>
                  <span>{p.generates_invoice ? "Invoiced" : "No invoice"}</span>
                  <span>{p.included_in_accounts ? "In accounts" : "Excluded"}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 shrink-0">
                {!p.is_default && !p.archived && (
                  <Button size="sm" variant="outline" onClick={() => setDefault(p.id)} data-testid={`pt-default-${p.code}`}>Set default</Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`pt-edit-${p.code}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => archive(p.id, p.archived)} className={p.archived ? "" : "text-destructive hover:text-destructive"} data-testid={`pt-archive-${p.code}`}>
                  {p.archived ? "Restore" : "Archive"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {(editing || creating) && (
        <PaymentTypeDialog
          initial={editing}
          isCreate={creating}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function PaymentTypeDialog({ initial, isCreate, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    name: "", code: "", tax_rate: 0, generates_invoice: true, included_in_accounts: true, is_default: false, off_the_books: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        tax_rate: Number(form.tax_rate || 0),
        generates_invoice: !!form.generates_invoice,
        included_in_accounts: !!form.included_in_accounts,
        is_default: !!form.is_default,
        off_the_books: !!form.off_the_books,
      };
      if (isCreate && form.code?.trim()) payload.code = form.code.trim();
      if (isCreate) {
        await api.post("/settings/payment-types", payload);
        toast.success("Payment type created");
      } else {
        await api.put(`/settings/payment-types/${initial.id}`, payload);
        toast.success("Payment type updated");
      }
      // Phase E — broadcast so dropdown listeners (BookingForm,
      // QuickBookingForm) refetch without a hard reload.
      try { window.dispatchEvent(new Event("jobsbook:payment-types-changed")); } catch { /* noop */ }
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2?.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-type-dialog">
        <DialogHeader>
          <DialogTitle>{isCreate ? "New payment type" : `Edit ${initial?.name}`}</DialogTitle>
          <DialogDescription>{isCreate ? "Code auto-generates from name if blank." : "Code is immutable after creation."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="pt-name-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold">Code</Label>
              <Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!isCreate} data-testid="pt-code-input" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider font-semibold">Tax rate (%)</Label>
            <Input type="number" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="pt-tax-input" />
          </div>
          <div className="flex flex-col gap-2 pt-1">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.generates_invoice} onCheckedChange={(v) => setForm({ ...form, generates_invoice: !!v })} data-testid="pt-gen-invoice-switch" />
              Generates invoice
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.included_in_accounts} onCheckedChange={(v) => setForm({ ...form, included_in_accounts: !!v })} data-testid="pt-in-accounts-switch" />
              Included in accounts
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: !!v })} data-testid="pt-default-switch" />
              Default for new bookings
            </label>
            <label className="flex items-start gap-2 text-sm border-t border-border pt-3 mt-1">
              <Checkbox
                checked={!!form.off_the_books}
                onCheckedChange={(v) => setForm((f) => ({
                  ...f,
                  off_the_books: !!v,
                  // Phase E — UX shortcut: ticking off-the-books forces
                  // both invoice + accounts flags off (server enforces the
                  // same linkage; mirroring it here avoids a confusing UI
                  // where they look enabled but the backend disables them).
                  generates_invoice: v ? false : f.generates_invoice,
                  included_in_accounts: v ? false : f.included_in_accounts,
                }))}
                data-testid="payment-type-off-the-books-checkbox"
              />
              <span className="flex flex-col gap-0.5">
                <span className="font-semibold">Off the books</span>
                <span className="text-[11px] text-muted-foreground leading-snug">
                  Bookings using this type skip invoicing and are excluded
                  from accounts export (like Simple Cash). Cash still
                  tracked in the ledger.
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="btn-cta" data-testid="pt-save-button">
              {submitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wider font-semibold">{label}</Label>
      {children}
    </div>
  );
}

function ReadOnly({ label, value, testId }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wider font-semibold">{label}</Label>
      <div className="bg-secondary/60 border border-border rounded-[6px] px-3 py-2 mono text-sm" data-testid={testId}>{value}</div>
    </div>
  );
}


function InvoiceTemplateEditor({ tpl, setInvTpl, addExtra, updateExtra, removeExtra, templateId }) {
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);

  // Debounced preview refresh on any template change.
  useEffect(() => {
    const t = setTimeout(async () => {
      setPreviewBusy(true);
      try {
        const { data: res } = await api.post("/invoice-templates/preview", {
          template_overrides: tpl, template_id: templateId, sample: true,
        });
        setPreviewHtml(res?.html || "");
      } catch {
        // Don't toast here — too noisy on every keystroke.
      } finally {
        setPreviewBusy(false);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tpl), templateId]);

  return (
    <div data-testid="invoice-template-editor">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* LEFT: form */}
        <div className="space-y-4" data-testid="invoice-template-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Accent color">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={tpl.accent_color || "#0ea5e9"}
                  onChange={(e) => setInvTpl("accent_color", e.target.value)}
                  className="h-10 w-12 rounded border border-border cursor-pointer"
                  data-testid="invoice-accent-color-picker"
                />
                <Input
                  value={tpl.accent_color || ""}
                  onChange={(e) => setInvTpl("accent_color", e.target.value)}
                  className="mono"
                  data-testid="invoice-accent-color-input"
                />
              </div>
            </Field>
            <Field label="Logo position">
              <Select
                value={tpl.logo_position || "top-left"}
                onValueChange={(v) => setInvTpl("logo_position", v)}
              >
                <SelectTrigger data-testid="invoice-logo-position-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="top-left" data-testid="logo-pos-top-left">Top-left</SelectItem>
                  <SelectItem value="top-center" data-testid="logo-pos-top-center">Top-center</SelectItem>
                  <SelectItem value="top-right" data-testid="logo-pos-top-right">Top-right</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="flex flex-wrap gap-6 items-center">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={!!tpl.show_vat_block}
                onCheckedChange={(v) => setInvTpl("show_vat_block", v)}
                data-testid="invoice-show-vat-switch"
              />
              <span>Show VAT breakdown</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={!!tpl.show_extra_fields}
                onCheckedChange={(v) => setInvTpl("show_extra_fields", v)}
                data-testid="invoice-show-extra-switch"
              />
              <span>Show extra fields block</span>
            </label>
          </div>

          <MarkdownArea
            label="Header (markdown)"
            testId="invoice-header-markdown"
            value={tpl.header_markdown || ""}
            onChange={(v) => setInvTpl("header_markdown", v)}
            showContactBlock
          />
          <MarkdownArea
            label="Body intro (above line items)"
            testId="invoice-body-intro-markdown"
            value={tpl.body_intro_markdown || ""}
            onChange={(v) => setInvTpl("body_intro_markdown", v)}
            showContactBlock
          />
          <MarkdownArea
            label="Body outro (below totals)"
            testId="invoice-body-outro-markdown"
            value={tpl.body_outro_markdown || ""}
            onChange={(v) => setInvTpl("body_outro_markdown", v)}
            showContactBlock
          />
          <MarkdownArea
            label="Payment terms"
            testId="invoice-payment-terms"
            value={tpl.payment_terms || ""}
            onChange={(v) => setInvTpl("payment_terms", v)}
          />
          <MarkdownArea
            label="Bank details"
            testId="invoice-bank-details"
            value={tpl.bank_details || ""}
            onChange={(v) => setInvTpl("bank_details", v)}
          />
          <MarkdownArea
            label="Footer (markdown)"
            testId="invoice-footer-markdown"
            value={tpl.footer_markdown || ""}
            onChange={(v) => setInvTpl("footer_markdown", v)}
          />

          <Field label="Accounts dept email">
            <Input
              type="email"
              value={tpl.accounts_dept_email || ""}
              onChange={(e) => setInvTpl("accounts_dept_email", e.target.value)}
              data-testid="invoice-accounts-email-input"
            />
          </Field>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider font-semibold">Extra fields</Label>
              <Button type="button" variant="outline" size="sm" onClick={addExtra} data-testid="add-extra-field">
                <Plus className="h-3 w-3" /> Add
              </Button>
            </div>
            {(tpl.extra_fields || []).length === 0 ? (
              <div className="text-xs text-muted-foreground">No extra fields configured.</div>
            ) : (
              tpl.extra_fields.map((f, i) => (
                <div key={i} className="flex gap-2">
                  <Input placeholder="Key" value={f.key || ""} onChange={(e) => updateExtra(i, "key", e.target.value)} data-testid={`extra-key-${i}`} />
                  <Input placeholder="Value" value={f.value || ""} onChange={(e) => updateExtra(i, "value", e.target.value)} data-testid={`extra-value-${i}`} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeExtra(i)} data-testid={`remove-extra-${i}`}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* RIGHT: live preview */}
        <div className="space-y-2" data-testid="invoice-template-preview">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wider font-semibold">Live preview</Label>
            {previewBusy && (
              <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Updating…</span>
            )}
          </div>
          <iframe
            title="invoice-preview"
            srcDoc={previewHtml}
            sandbox=""
            className="w-full h-[860px] bg-white border border-border rounded-[8px] shadow-inner"
            data-testid="invoice-preview-iframe"
          />
          <p className="text-[11px] text-muted-foreground">
            Preview uses sample data (real customer + your company snapshot). Tokens like
            <code className="mono mx-1">{"{{customer_company}}"}</code> are replaced live.
          </p>
        </div>
      </div>
    </div>
  );
}

function InvoiceTemplatesPane() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // template object or {isNew: true}
  const [duplicating, setDuplicating] = useState(null);
  const [duplicateName, setDuplicateName] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/invoice-templates");
      setTemplates(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onActivate = async (tpl) => {
    try {
      await api.post(`/invoice-templates/${tpl.id}/activate`);
      toast.success(`"${tpl.name}" is now in use`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const onDelete = async (tpl) => {
    try {
      await api.delete(`/invoice-templates/${tpl.id}`);
      toast.success(`Deleted "${tpl.name}"`);
      setDeleting(null);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const onDuplicateConfirm = async () => {
    if (!duplicating) return;
    try {
      const { data } = await api.post(`/invoice-templates/${duplicating.id}/duplicate`, { name: duplicateName.trim() || undefined });
      toast.success(`Created "${data.name}"`);
      setDuplicating(null);
      setDuplicateName("");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <section className="bg-card border border-border rounded-[8px] p-6" data-testid="settings-invoice-templates">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-extrabold">Invoice templates</h2>
          <p className="text-xs text-muted-foreground mt-1">Each new invoice snapshots the template marked “In use”. Historic invoices keep their original template.</p>
        </div>
        <Button type="button" onClick={() => setEditing({ isNew: true })} className="btn-cta" data-testid="invoice-template-create">
          <Plus className="h-4 w-4 mr-1" /> Create new template
        </Button>
      </div>

      <div className="mt-4 space-y-2" data-testid="invoice-templates-list">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : templates.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">No templates yet. Create one to get started.</div>
        ) : templates.map((t) => (
          <div key={t.id} className={`flex items-center gap-3 border ${t.is_standard ? "border-sky-200 bg-sky-50/40" : "border-border"} rounded-[6px] p-3 flex-wrap`} data-testid={`invoice-template-card-${t.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold">{t.name}</span>
                {t.is_standard && (
                  <span className="mono text-[10px] uppercase tracking-widest bg-sky-100 text-sky-800 border border-sky-200 px-1.5 py-0.5 rounded" data-testid={`invoice-template-badge-standard-${t.id}`}>Standard</span>
                )}
                {t.is_active && (
                  <span className="mono text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded" data-testid={`invoice-template-badge-active-${t.id}`}>In use</span>
                )}
                {t.is_seed && !t.is_standard && (
                  <span className="mono text-[10px] uppercase tracking-widest bg-sky-100 text-sky-800 border border-sky-200 px-1.5 py-0.5 rounded">Seed</span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {t.is_standard
                  ? "Platform default — read-only. Managed by JobsBook admins."
                  : `Created ${(t.created_at || "").slice(0, 10)}`}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setEditing(t)}
                data-testid={`invoice-template-edit-${t.id}`}
              >
                {t.is_standard ? "Preview" : "Edit"}
              </Button>
              {!t.is_standard && (
                <Button type="button" variant="outline" size="sm" onClick={() => { setDuplicating(t); setDuplicateName(`Copy of ${t.name}`); }} data-testid={`invoice-template-duplicate-${t.id}`}>Duplicate</Button>
              )}
              {!t.is_active && (
                <Button type="button" size="sm" onClick={() => onActivate(t)} className="btn-cta" data-testid={`invoice-template-activate-${t.id}`}>Set as in use</Button>
              )}
              {!t.is_standard && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={templates.length <= 1}
                  title={templates.length <= 1 ? "Cannot delete the last remaining template" : "Delete"}
                  onClick={() => setDeleting(t)}
                  className="text-rose-700 hover:text-rose-700"
                  data-testid={`invoice-template-delete-${t.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="invoice-template-editor-dialog">
          <InvoiceTemplateEditDialog
            template={editing && !editing.isNew ? editing : null}
            isNew={editing?.isNew}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!duplicating} onOpenChange={(o) => !o && setDuplicating(null)}>
        <DialogContent data-testid="invoice-template-duplicate-dialog">
          <DialogHeader>
            <DialogTitle>Duplicate template</DialogTitle>
            <DialogDescription>Pick a name for the new template.</DialogDescription>
          </DialogHeader>
          <Input
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            placeholder="Template name"
            data-testid="invoice-template-duplicate-name-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicating(null)}>Cancel</Button>
            <Button onClick={onDuplicateConfirm} className="btn-cta" data-testid="invoice-template-duplicate-confirm">Duplicate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent data-testid="invoice-template-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete &ldquo;{deleting?.name}&rdquo;?</DialogTitle>
            <DialogDescription>
              This cannot be undone. Historic invoices using this template keep their original snapshot — they will still render correctly.
              {deleting?.is_active && " Another template will automatically become 'in use'."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button onClick={() => onDelete(deleting)} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="invoice-template-delete-confirm">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function InvoiceTemplateEditDialog({ template, isNew, onClose, onSaved }) {
  const [tpl, setTpl] = useState(template || {
    name: "",
    header_text: "", footer_text: "",
    header_markdown: "", body_intro_markdown: "", body_outro_markdown: "", footer_markdown: "",
    payment_terms: "", bank_details: "",
    accent_color: "#0ea5e9", logo_position: "top-left",
    show_vat_block: true, show_extra_fields: true,
    extra_fields: [], accounts_dept_email: "",
    is_active: false,
  });
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setTpl((t) => ({ ...t, [k]: v }));
  const addExtra = () => setTpl((t) => ({ ...t, extra_fields: [...(t.extra_fields || []), { key: "", value: "" }] }));
  const updateExtra = (i, k, v) => setTpl((t) => {
    const arr = [...(t.extra_fields || [])];
    arr[i] = { ...arr[i], [k]: v };
    return { ...t, extra_fields: arr };
  });
  const removeExtra = (i) => setTpl((t) => ({ ...t, extra_fields: (t.extra_fields || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!(tpl.name || "").trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/invoice-templates", tpl);
        toast.success("Template created");
      } else {
        await api.put(`/invoice-templates/${template.id}`, tpl);
        toast.success("Template saved");
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div>
      <DialogHeader>
        <DialogTitle>{isNew ? "Create new template" : `Edit ${template?.name}`}</DialogTitle>
        <DialogDescription>Live preview shows sample data · auto-snapshotted onto each new invoice.</DialogDescription>
      </DialogHeader>
      <div className="mt-2 mb-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
        <Field label="Template name">
          <Input
            value={tpl.name || ""}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="e.g. Formal VAT, Simple receipt"
            data-testid="invoice-template-name-input"
            autoFocus
          />
        </Field>
        <label className="flex items-center gap-2 text-sm mb-1">
          <Switch checked={!!tpl.is_active} onCheckedChange={(v) => setField("is_active", !!v)} data-testid="invoice-template-isactive-switch" />
          <span>Set as in use</span>
        </label>
      </div>
      <InvoiceTemplateEditor
        tpl={tpl}
        setInvTpl={setField}
        addExtra={addExtra}
        updateExtra={updateExtra}
        removeExtra={removeExtra}
        templateId={template?.id}
      />
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving} className="btn-cta" data-testid="invoice-template-editor-save">
          {saving ? "Saving..." : isNew ? "Create template" : "Save changes"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function MarkdownArea({ label, value, onChange, testId, showContactBlock = false }) {
  const ref = useRef(null);
  const insertAtCursor = (snippet) => {
    const el = ref.current;
    if (!el) { onChange((value || "") + snippet); return; }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const next = (value || "").slice(0, start) + snippet + (value || "").slice(end);
    onChange(next);
    setTimeout(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  };
  const insertToken = (token) => insertAtCursor(`{{${token}}}`);
  // Phase 39 — one-click contact block. Each line ends with two trailing
  // spaces so Markdown renders a hard line break inside one paragraph,
  // keeping the contact details visually grouped on the invoice.
  const CONTACT_BLOCK = [
    "Tel: {{company_phone}}  ",
    "Mob: {{company_mobile}}  ",
    "Email: {{company_email}}  ",
    "Web: {{company_website}}  ",
    "Reg: {{company_registration_number}}  ",
    "{{company_tagline}}",
  ].join("\n");
  const insertContactBlock = () => insertAtCursor(CONTACT_BLOCK);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label className="text-xs uppercase tracking-wider font-semibold">{label}</Label>
        <div className="flex items-center gap-2">
          {showContactBlock && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={insertContactBlock}
              data-testid={`${testId}-insert-contact-block`}
              title="Insert a ready-made multi-line contact block (phone / mobile / email / web / reg / tagline)"
            >
              <Clipboard className="h-3 w-3 mr-1" aria-hidden="true" />
              Insert default contact block
            </Button>
          )}
          <Select onValueChange={insertToken} value="">
            <SelectTrigger className="h-7 w-[170px] text-xs" data-testid={`${testId}-token-trigger`}>
              <SelectValue placeholder="Insert token..." />
            </SelectTrigger>
            <SelectContent data-testid={`${testId}-token-list`}>
              {TOKEN_LIST.map((t) => (
                <SelectItem key={t.token} value={t.token} data-testid={`${testId}-token-${t.token}`}>
                  <span className="mono">{`{{${t.token}}}`}</span> · <span className="text-muted-foreground">{t.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Textarea
        ref={ref}
        rows={3}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mono text-xs"
        data-testid={testId}
        placeholder="Markdown supported · paragraphs, **bold**, *italic*, lists, links"
      />
    </div>
  );
}


function EmailTemplatesPane() {
  const [templates, setTemplates] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/email-templates"),
        api.get("/email-templates/tokens").catch(() => ({ data: { tokens: [] } })),
      ]);
      setTemplates(a.data || []);
      setTokens(b.data?.tokens || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <section className="bg-card border border-border rounded-[8px] p-6" data-testid="settings-email-templates">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-extrabold">Email templates</h2>
          <p className="text-xs text-muted-foreground mt-1">Notification emails sent to customers — booking confirmations and payment receipts.</p>
        </div>
      </div>
      <div className="mt-4 space-y-2" data-testid="email-templates-list">
        {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : templates.map((t) => (
          <div key={t.id} className="flex items-center gap-3 border border-border rounded-[6px] p-3 flex-wrap" data-testid={`email-template-card-${t.code}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold">{t.name}</span>
                <span className="mono text-[10px] uppercase tracking-widest bg-secondary text-foreground border border-border px-1.5 py-0.5 rounded">{t.code}</span>
                {!t.enabled && <span className="mono text-[10px] uppercase tracking-widest bg-rose-100 text-rose-800 border border-rose-200 px-1.5 py-0.5 rounded">Disabled</span>}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{t.subject}</div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setEditing(t)} data-testid={`email-template-edit-${t.code}`}>Edit</Button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="email-template-editor-dialog">
          {editing && (
            <EmailTemplateEditDialog
              template={editing}
              tokens={tokens}
              onClose={() => setEditing(null)}
              onSaved={() => { setEditing(null); load(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function EmailTemplateEditDialog({ template, tokens, onClose, onSaved }) {
  const [tpl, setTpl] = useState({
    name: template.name || "",
    subject: template.subject || "",
    body_markdown: template.body_markdown || "",
    enabled: !!template.enabled,
  });
  const [saving, setSaving] = useState(false);
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");

  // Live preview debounced
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/email-templates/preview", {
          code: template.code,
          template_overrides: { subject: tpl.subject, body_markdown: tpl.body_markdown, name: tpl.name, enabled: tpl.enabled },
        });
        setPreviewSubject(data.subject);
        setPreviewHtml(data.html);
      } catch {/* ignore */}
    }, 300);
    return () => clearTimeout(t);
  }, [tpl, template.code]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/email-templates/${template.id}`, tpl);
      toast.success("Template saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const insertToken = (token) => {
    setTpl((t) => ({ ...t, body_markdown: (t.body_markdown || "") + `{{${token}}}` }));
  };

  return (
    <div>
      <DialogHeader>
        <DialogTitle>Edit {template.name}</DialogTitle>
        <DialogDescription>{template.code} · live preview shows sample data.</DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-4">
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Subject</Label>
            <Input value={tpl.subject} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })} data-testid="email-template-subject-input" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Body (Markdown)</Label>
              <Select onValueChange={insertToken}>
                <SelectTrigger className="h-7 w-44 text-xs" data-testid="email-template-insert-token">
                  <SelectValue placeholder="Insert token..." />
                </SelectTrigger>
                <SelectContent>
                  {tokens.map((t) => <SelectItem key={t.token} value={t.token}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Textarea rows={14} value={tpl.body_markdown} onChange={(e) => setTpl({ ...tpl, body_markdown: e.target.value })} data-testid="email-template-body-input" className="mono text-xs" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={tpl.enabled} onCheckedChange={(v) => setTpl({ ...tpl, enabled: !!v })} data-testid="email-template-enabled-switch" />
            <span>Enabled</span>
          </label>
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Preview</Label>
          <div className="border border-border rounded-[6px] p-3 bg-secondary/30">
            <div className="text-[11px] mono uppercase tracking-widest text-muted-foreground">Subject</div>
            <div className="text-sm font-bold mb-3" data-testid="email-template-preview-subject">{previewSubject || "—"}</div>
            <div className="text-[11px] mono uppercase tracking-widest text-muted-foreground">Body</div>
            <div className="prose prose-sm max-w-none bg-white rounded p-3 border border-border" data-testid="email-template-preview-body" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </div>
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving} className="btn-cta" data-testid="email-template-save">
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </DialogFooter>
    </div>
  );
}


// ----- Phase 62.2 — Editable Email Defaults (subject / greeting / body / signature) -----
const EMAIL_DEFAULT_FALLBACK = {
  subject: "Invoice {{invoice_number}} from {{company_name}}",
  greeting: "Hi {{customer_name}},",
  body:
    "Thanks for your business — please find invoice {{invoice_number}} attached for {{total}}.\n\n" +
    "Due date: {{due_date}}.\n\n" +
    "If you have any questions about this invoice, just reply to this email and we'll get back to you.",
  signature: "Best,\n{{company_name}}\n{{company_phone}} · {{company_email}}",
};

const EMAIL_DEFAULT_TOKENS = [
  { token: "invoice_number", label: "Invoice #" },
  { token: "total", label: "Total" },
  { token: "due_date", label: "Due date" },
  { token: "issued_date", label: "Issued date" },
  { token: "customer_name", label: "Customer name" },
  { token: "customer_company", label: "Customer company" },
  { token: "customer_contact", label: "Customer contact" },
  { token: "company_name", label: "Company name" },
  { token: "company_phone", label: "Company phone" },
  { token: "company_email", label: "Company email" },
  { token: "payment_type_name", label: "Payment type" },
];

// Tiny client-side renderer mirroring the backend `{{token}}` substitution so
// the preview matches what the customer will receive.
function renderEmailPreview(text, ctx) {
  if (!text) return "";
  return String(text).replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
    const v = ctx[key];
    return v === undefined || v === null ? "" : String(v);
  });
}

function EmailDefaultsPane({ disabled = false }) {
  const SAMPLE = {
    invoice_number: "I-000123",
    total: "€1,234.56",
    due_date: "2026-03-15",
    issued_date: "2026-02-13",
    customer_name: "Acme Ltd",
    customer_company: "Acme Ltd",
    customer_contact: "Jane Doe",
    company_name: "Your Company",
    company_phone: "+353 1 555 0100",
    company_email: "info@yourdomain.com",
    payment_type_name: "VAT (23%)",
  };
  const [tpl, setTpl] = useState(EMAIL_DEFAULT_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [insertTarget, setInsertTarget] = useState("body");
  const subjectRef = useRef(null);
  const greetingRef = useRef(null);
  const bodyRef = useRef(null);
  const signatureRef = useRef(null);
  const refFor = (k) => ({
    subject: subjectRef,
    greeting: greetingRef,
    body: bodyRef,
    signature: signatureRef,
  }[k] || bodyRef);

  useEffect(() => {
    api.get("/invoice-templates/email")
      .then(({ data }) => setTpl({ ...EMAIL_DEFAULT_FALLBACK, ...(data || {}) }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setField = (k, v) => setTpl((t) => ({ ...t, [k]: v }));

  const insertToken = (token) => {
    if (disabled) return;
    const field = insertTarget;
    const ref = refFor(field);
    const insertText = `{{${token}}}`;
    const el = ref.current;
    if (!el) {
      setField(field, (tpl[field] || "") + insertText);
      return;
    }
    const start = el.selectionStart ?? (tpl[field] || "").length;
    const end = el.selectionEnd ?? start;
    const current = tpl[field] || "";
    const next = current.slice(0, start) + insertText + current.slice(end);
    setField(field, next);
    requestAnimationFrame(() => {
      try {
        el.focus();
        const caret = start + insertText.length;
        el.setSelectionRange(caret, caret);
      } catch (_e) { /* ignore */ }
    });
  };

  const resetToDefaults = () => {
    if (disabled) return;
    setTpl({ ...EMAIL_DEFAULT_FALLBACK });
  };

  const save = async () => {
    if (disabled) return;
    setSaving(true);
    try {
      const { data } = await api.put("/invoice-templates/email", tpl);
      setTpl({ ...EMAIL_DEFAULT_FALLBACK, ...(data || {}) });
      toast.success("Email defaults saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const previewSubject = renderEmailPreview(tpl.subject || "", SAMPLE);
  const previewBodyText = [
    renderEmailPreview(tpl.greeting || "", SAMPLE),
    renderEmailPreview(tpl.body || "", SAMPLE),
    renderEmailPreview(tpl.signature || "", SAMPLE),
  ].filter((s) => (s || "").trim().length > 0).join("\n\n");

  return (
    <section
      className="bg-card border border-border rounded-[8px] p-6 space-y-4"
      data-testid="settings-email-defaults"
    >
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-extrabold">Email defaults</h2>
          <p className="text-[12px] text-muted-foreground mt-1 max-w-2xl">
            These compose the email body when you click &quot;Send&quot; on an invoice.
            Each field accepts <span className="mono">{"{{tokens}}"}</span> — the
            preview below uses sample values. Override any of these on a
            per-send basis from the Send dialog without touching the defaults.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={resetToDefaults}
            disabled={disabled || loading}
            data-testid="email-defaults-reset"
            className="text-[11px] mono"
          >
            Reset to defaults
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={disabled || loading || saving}
            className="btn-cta"
            data-testid="email-defaults-save"
          >
            {saving ? "Saving…" : "Save email defaults"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: editor */}
        <div className="space-y-4">
          {/* Variable picker chips */}
          <div className="space-y-2" data-testid="email-defaults-variable-picker">
            <div className="text-[11px] mono uppercase tracking-widest text-muted-foreground flex items-center gap-2 flex-wrap">
              <span>Insert variable into</span>
              <Select value={insertTarget} onValueChange={setInsertTarget}>
                <SelectTrigger
                  className="h-7 w-36 text-xs"
                  data-testid="email-defaults-insert-target"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="subject">Subject</SelectItem>
                  <SelectItem value="greeting">Greeting</SelectItem>
                  <SelectItem value="body">Body</SelectItem>
                  <SelectItem value="signature">Signature</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {EMAIL_DEFAULT_TOKENS.map((t) => (
                <button
                  type="button"
                  key={t.token}
                  onClick={() => insertToken(t.token)}
                  disabled={disabled}
                  className="text-[11px] mono px-2 py-1 rounded border border-border bg-secondary hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`email-defaults-token-${t.token}`}
                  title={`{{${t.token}}}`}
                >
                  {"{{"}{t.token}{"}}"}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold">Subject</label>
            <Input
              ref={subjectRef}
              value={tpl.subject || ""}
              onChange={(e) => setField("subject", e.target.value)}
              maxLength={240}
              disabled={disabled || loading}
              placeholder={EMAIL_DEFAULT_FALLBACK.subject}
              data-testid="email-defaults-subject"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold">Greeting</label>
            <Input
              ref={greetingRef}
              value={tpl.greeting || ""}
              onChange={(e) => setField("greeting", e.target.value)}
              maxLength={240}
              disabled={disabled || loading}
              placeholder={EMAIL_DEFAULT_FALLBACK.greeting}
              data-testid="email-defaults-greeting"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold">Body</label>
            <Textarea
              ref={bodyRef}
              value={tpl.body || ""}
              onChange={(e) => setField("body", e.target.value)}
              rows={8}
              maxLength={8000}
              disabled={disabled || loading}
              placeholder={EMAIL_DEFAULT_FALLBACK.body}
              className="font-mono text-xs"
              data-testid="email-defaults-body"
            />
            <div className="text-[11px] text-muted-foreground">
              {(tpl.body || "").length}/8000 chars
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold">Signature</label>
            <Textarea
              ref={signatureRef}
              value={tpl.signature || ""}
              onChange={(e) => setField("signature", e.target.value)}
              rows={4}
              maxLength={1000}
              disabled={disabled || loading}
              placeholder={EMAIL_DEFAULT_FALLBACK.signature}
              className="font-mono text-xs"
              data-testid="email-defaults-signature"
            />
          </div>
        </div>

        {/* Right: live preview */}
        <div className="space-y-2" data-testid="email-defaults-preview">
          <div className="text-[11px] mono uppercase tracking-widest text-muted-foreground">
            Live preview · sample data
          </div>
          <div className="border border-border rounded-[6px] p-4 bg-secondary/30 space-y-3">
            <div>
              <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">
                Subject
              </div>
              <div
                className="text-sm font-bold mt-0.5"
                data-testid="email-defaults-preview-subject"
              >
                {previewSubject || "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">
                Body
              </div>
              <pre
                className="bg-card border border-border rounded p-3 text-xs leading-relaxed whitespace-pre-wrap font-sans mt-1"
                data-testid="email-defaults-preview-body"
              >
                {previewBodyText || "—"}
              </pre>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Sample values are illustrative. Real sends use the recipient
            customer + invoice data.
          </p>
        </div>
      </div>
    </section>
  );
}

// ----- Phase 63.1 — Daily Backup Configuration pane -----------------------
// Renders inside Settings between Invoice Settings and Notifications. Loads
// /admin/backup/config + /admin/backup/history independently of the main
// settings PUT so toggling a switch doesn't require a full Save.
const BACKUP_SHEET_OPTIONS = [
  { key: "bookings", label: "Bookings" },
  { key: "invoices", label: "Invoices" },
  { key: "customers", label: "Customers" },
  { key: "staff_ledger", label: "Staff Cash Ledger" },
  { key: "manager_ledger", label: "Manager Cash Ledger" },
  { key: "payment_approvals", label: "Payment Approvals" },
];
const BACKUP_TIMEZONES = [
  "Europe/Dublin", "Europe/London", "Europe/Paris", "Europe/Madrid",
  "Europe/Berlin", "Europe/Lisbon", "America/New_York", "America/Los_Angeles",
  "Asia/Singapore", "Australia/Sydney", "UTC",
];

function formatBackupBytes(b) {
  if (!b && b !== 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

function DailyBackupPane({ disabled = false }) {
  const [cfg, setCfg] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const refresh = async () => {
    try {
      const [{ data: c }, { data: h }] = await Promise.all([
        api.get("/admin/backup/config"),
        api.get("/admin/backup/history", { params: { limit: 10 } }),
      ]);
      setCfg(c);
      setHistory(Array.isArray(h) ? h : []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const setField = (k, v) => setCfg((c) => ({ ...(c || {}), [k]: v }));
  const setSheet = (k, v) =>
    setCfg((c) => ({ ...(c || {}), sheets_enabled: { ...((c || {}).sheets_enabled || {}), [k]: !!v } }));

  const save = async () => {
    if (disabled || !cfg) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/backup/config", {
        email: cfg.email || "",
        time: cfg.time || "06:00",
        timezone: cfg.timezone || "Europe/Dublin",
        enabled: !!cfg.enabled,
        sheets_enabled: cfg.sheets_enabled || {},
      });
      setCfg(data);
      toast.success("Backup settings saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    if (disabled) return;
    setSending(true);
    try {
      await api.post("/admin/backup/send-now");
      toast.success("Backup sent — check your inbox");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (loading || !cfg) {
    return (
      <section className="bg-card border border-border rounded-[8px] p-6" data-testid="settings-daily-backup">
        <div className="text-sm text-muted-foreground">Loading backup settings…</div>
      </section>
    );
  }

  const sheets = cfg.sheets_enabled || {};

  return (
    <section className="bg-card border border-border rounded-[8px] p-6 space-y-5" data-testid="settings-daily-backup">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-extrabold">Daily Backup</h2>
          <p className="text-[12px] text-muted-foreground mt-1 max-w-2xl">
            Get an Excel snapshot of your tenant data emailed every day. Keep it
            somewhere safe — if the system ever goes down, this is your fallback
            copy of bookings, invoices, customers and ledgers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={sendNow}
                  disabled={disabled || sending} data-testid="backup-send-now">
            {sending ? "Sending…" : "Send backup now"}
          </Button>
          <Button type="button" onClick={save}
                  disabled={disabled || saving} className="btn-cta" data-testid="backup-save">
            {saving ? "Saving…" : "Save backup settings"}
          </Button>
        </div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <Switch
          checked={!!cfg.enabled}
          onCheckedChange={(v) => setField("enabled", !!v)}
          disabled={disabled}
          data-testid="backup-enabled-toggle"
        />
        <span className="text-sm leading-tight">
          <span className="font-semibold">Enable scheduled daily backup</span>
          <span className="block text-[11px] text-muted-foreground mt-0.5">
            We&apos;ll send your backup once per day at the time below.
          </span>
        </span>
      </label>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1 md:col-span-2">
          <label className="text-[11px] mono uppercase tracking-wider text-muted-foreground">
            Recipient email
          </label>
          <Input
            value={cfg.email || ""}
            onChange={(e) => setField("email", e.target.value)}
            disabled={disabled || !cfg.enabled}
            placeholder="you@yourbusiness.com"
            data-testid="backup-email-input"
          />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] mono uppercase tracking-wider text-muted-foreground">
            Time (24h)
          </label>
          <Input
            type="time"
            value={cfg.time || "06:00"}
            onChange={(e) => setField("time", e.target.value)}
            disabled={disabled || !cfg.enabled}
            data-testid="backup-time-input"
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[11px] mono uppercase tracking-wider text-muted-foreground">
          Timezone
        </label>
        <Select
          value={cfg.timezone || "Europe/Dublin"}
          onValueChange={(v) => setField("timezone", v)}
          disabled={disabled || !cfg.enabled}
        >
          <SelectTrigger data-testid="backup-timezone-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {BACKUP_TIMEZONES.map((tz) => (
              <SelectItem key={tz} value={tz} data-testid={`backup-timezone-${tz.replace("/", "-")}`}>{tz}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2" data-testid="backup-sheets-block">
        <div className="text-[11px] mono uppercase tracking-wider text-muted-foreground">
          Sheets to include
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {BACKUP_SHEET_OPTIONS.map((opt) => (
            <label key={opt.key} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={sheets[opt.key] !== false}
                onCheckedChange={(v) => setSheet(opt.key, !!v)}
                disabled={disabled || !cfg.enabled}
                data-testid={`backup-sheet-${opt.key}`}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-extrabold">Recent backups</h3>
          <span className="text-[10px] mono uppercase tracking-widest text-muted-foreground">
            Last {history.length}
          </span>
        </div>
        {history.length === 0 ? (
          <div className="text-xs text-muted-foreground italic" data-testid="backup-history-empty">
            No backups yet. Click <strong>Send backup now</strong> to fire one
            on demand, or wait for the next scheduled run.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="backup-history-table">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary/40">
                <tr>
                  <th className="text-left px-2 py-1.5">Date</th>
                  <th className="text-left px-2 py-1.5">Status</th>
                  <th className="text-left px-2 py-1.5">Trigger</th>
                  <th className="text-left px-2 py-1.5">Recipient</th>
                  <th className="text-right px-2 py-1.5">Size</th>
                </tr>
              </thead>
              <tbody>
                {history.map((j) => (
                  <tr key={j.id} className="border-t border-border" data-testid={`backup-history-row-${j.id}`}>
                    <td className="px-2 py-1.5 mono text-[11px]">
                      {(j.started_at || "").slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`text-[10px] mono uppercase tracking-widest px-1.5 py-0.5 rounded ${
                        j.status === "sent" ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                        : j.status === "failed" ? "bg-rose-100 text-rose-800 border border-rose-200"
                        : "bg-slate-100 text-slate-800 border border-slate-200"
                      }`}>
                        {j.status}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground">{j.triggered_by}</td>
                    <td className="px-2 py-1.5 truncate max-w-[220px]">{j.recipient_email}</td>
                    <td className="px-2 py-1.5 text-right mono tabular-nums">{formatBackupBytes(j.file_size_bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}


// ----- Phase 66 — Stripe Connect live-mode readiness diagnostic ----------
// Reads /api/admin/billing/connect/status and renders the 6-item env-var
// checklist + the calling tenant's Connect account state. Also exposes a
// "Refresh invoice snapshots" CTA wired to POST
// /api/admin/invoices/refresh-snapshots.
function StripeConnectCard({ disabled = false }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshOpen, setRefreshOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/billing/connect/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const runRefreshSnapshots = async () => {
    if (disabled) return;
    setRefreshing(true);
    try {
      const { data } = await api.post(
        "/admin/invoices/refresh-snapshots?dry_run=false&delete_cached_pdfs=true",
      );
      setLastRefresh(data);
      toast.success(
        data.cleaned > 0
          ? `Refreshed ${data.cleaned} invoice${data.cleaned === 1 ? "" : "s"} · ${data.pdf_wiped} PDF${data.pdf_wiped === 1 ? "" : "s"} re-rendered`
          : "All invoices are already clean — nothing to refresh.",
      );
      setRefreshOpen(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <section className="bg-card border border-border rounded-[8px] p-6" data-testid="settings-stripe-connect-card">
        <div className="text-sm text-muted-foreground italic">Loading Stripe diagnostic…</div>
      </section>
    );
  }
  if (!status) return null;

  const stubMode = !!status.stub_mode;
  const platformReady = !!status.platform_configured;
  const tenant = status.tenant || {};
  const tenantConnected = !!tenant.account_id;

  return (
    <section
      className="bg-card border border-border rounded-[8px] p-6 space-y-5"
      data-testid="settings-stripe-connect-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-extrabold">Stripe Connect — Live mode readiness</h2>
          <p className="text-[12px] text-muted-foreground mt-1 max-w-2xl">
            Six platform-side environment variables drive Pay-Online + customer
            checkout. Green ticks across the board = ready to take real money.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={refresh}
          className="text-[11px]"
          data-testid="stripe-connect-refresh"
        >
          <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Refresh status
        </Button>
      </div>

      {/* Top banner */}
      {stubMode ? (
        <div className="rounded-[6px] p-3 border bg-amber-50 border-amber-200 text-amber-900 text-sm" data-testid="stripe-connect-banner-stub">
          <strong>Currently in test / stub mode.</strong> Payments are simulated —
          no money actually moves. Unset <code className="mono">STRIPE_CONNECT_FORCE_STUB</code> (or set it to <code className="mono">0</code>) in the env-var dashboard, then redeploy.
        </div>
      ) : !platformReady ? (
        <div className="rounded-[6px] p-3 border bg-rose-50 border-rose-200 text-rose-900 text-sm" data-testid="stripe-connect-banner-missing">
          <strong>Platform credentials missing.</strong> See the checklist below —
          fill in any red row from the Stripe Dashboard, then redeploy.
        </div>
      ) : (
        <div className="rounded-[6px] p-3 border bg-emerald-50 border-emerald-200 text-emerald-900 text-sm" data-testid="stripe-connect-banner-ready">
          <strong>Live mode ready.</strong> All platform credentials are in place.
        </div>
      )}

      {/* Checklist */}
      <div className="border border-border rounded-[6px] divide-y divide-border" data-testid="stripe-connect-checklist">
        {(status.checklist || []).map((item) => (
          <div
            key={item.key}
            className="flex items-start gap-3 px-4 py-3"
            data-testid={`stripe-connect-row-${item.key}`}
          >
            <div className="mt-0.5 shrink-0">
              {item.ok ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600" data-testid={`stripe-connect-row-${item.key}-ok`} />
              ) : (
                <XCircle className="h-5 w-5 text-rose-600" data-testid={`stripe-connect-row-${item.key}-missing`} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-sm">{item.label}</span>
                <span className="mono text-[10px] uppercase tracking-widest bg-secondary/60 border border-border px-1.5 py-0.5 rounded">
                  {item.env_var}
                </span>
              </div>
              <p className="text-[12px] text-muted-foreground mt-1">{item.purpose}</p>
              {!item.ok && (
                <p className="text-[11px] text-rose-700 mt-1">
                  <strong>Where to get it:</strong> {item.where}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Tenant state */}
      <div className="border border-border rounded-[6px] p-4 space-y-2" data-testid="stripe-connect-tenant-state">
        <div className="text-[11px] mono uppercase tracking-widest text-muted-foreground font-bold">
          This tenant&apos;s Stripe Connect account
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
          <TenantRow label="Account linked" ok={tenantConnected} extra={tenantConnected ? tenant.account_id : "—"} testId="tenant-account-id" />
          <TenantRow label="Charges enabled" ok={!!tenant.charges_enabled} testId="tenant-charges" />
          <TenantRow label="Payouts enabled" ok={!!tenant.payouts_enabled} testId="tenant-payouts" />
        </div>
        {!tenantConnected && (
          <div className="pt-2">
            <a
              href="/billing"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-sky-700 hover:text-sky-900"
              data-testid="stripe-connect-cta"
            >
              Connect with Stripe <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <p className="text-[11px] text-muted-foreground mt-1">
              Takes ~5 minutes — Stripe will ask for your bank details + ID.
            </p>
          </div>
        )}
      </div>

      {/* Refresh invoice snapshots */}
      <div className="border-t border-border pt-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="max-w-xl">
            <h3 className="text-sm font-extrabold">Refresh invoice snapshots</h3>
            <p className="text-[12px] text-muted-foreground mt-1">
              Re-renders existing invoices through the placeholder sanitizer
              (Phase 64.1). Use this once after a redeploy to scrub any
              <span className="mono"> TEST_Company</span> /
              <span className="mono"> IE9999</span> values that were baked in
              before the sanitizer was wired. Idempotent.
            </p>
            {lastRefresh && (
              <p className="text-[11px] text-emerald-700 mt-1" data-testid="stripe-connect-last-refresh">
                Last run: scanned {lastRefresh.scanned}, polluted {lastRefresh.polluted},
                cleaned {lastRefresh.cleaned}, PDFs wiped {lastRefresh.pdf_wiped}.
              </p>
            )}
          </div>
          <Dialog open={refreshOpen} onOpenChange={(o) => !refreshing && setRefreshOpen(o)}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setRefreshOpen(true)}
              disabled={disabled}
              data-testid="stripe-connect-refresh-snapshots-btn"
            >
              <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Refresh invoice snapshots
            </Button>
            <DialogContent data-testid="refresh-snapshots-confirm-dialog">
              <DialogHeader>
                <DialogTitle>Refresh invoice snapshots?</DialogTitle>
                <DialogDescription>
                  Walks every invoice in this tenant, scrubs any placeholder
                  values (TEST_Company / TEST ADDRESS / IE9999 etc.) out of
                  their stored company snapshot, and deletes the cached PDF
                  so the next view re-renders cleanly. Safe to run anytime.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setRefreshOpen(false)} disabled={refreshing}>
                  Cancel
                </Button>
                <Button
                  onClick={runRefreshSnapshots}
                  disabled={refreshing}
                  className="bg-sky-500 hover:bg-sky-600 text-white"
                  data-testid="refresh-snapshots-confirm"
                >
                  {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCcw className="h-4 w-4 mr-1" /> Run refresh</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
}

function TenantRow({ label, ok, extra = null, testId }) {
  return (
    <div className="flex items-center gap-2 bg-secondary/30 rounded px-2 py-1.5" data-testid={testId}>
      {ok ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 text-rose-500 shrink-0" />
      )}
      <span className="text-[12px] font-semibold flex-1 truncate">{label}</span>
      {extra && (
        <span className="text-[10px] mono text-muted-foreground truncate max-w-[140px]">{extra}</span>
      )}
    </div>
  );
}


// Phase P.7.1 follow-up — removed `BulkRebuildInvoicesButton` component.
// Every /pdf-stream + /pdf read now force-renders with the P.7 live
// overlay so the bulk button is redundant. Backend
// `/invoices/bulk-rebuild-from-live` endpoint kept for admin debugging.


// Phase K — Base / depot address card. Pulls live values from the parent
// settings doc and writes via the dedicated /api/settings/base-address
// endpoint so the geocoder can resolve lat/lng before we update the cache.
function BaseAddressBlock({ data, reload }) {
  const [address, setAddress] = useState(data?.base_address || "");
  const [postcode, setPostcode] = useState(data?.base_postcode || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setAddress(data?.base_address || "");
    setPostcode(data?.base_postcode || "");
  }, [data?.base_address, data?.base_postcode]);

  const hasCoords = data?.base_lat != null && data?.base_lng != null;
  const dirty = address !== (data?.base_address || "") || postcode !== (data?.base_postcode || "");

  const save = async () => {
    setSaving(true);
    try {
      const { data: out } = await api.put("/settings/base-address", { address, postcode });
      if (out?.base_lat == null && address.trim()) {
        toast.warning("Saved, but the address could not be geocoded. Try adding a postcode or city.");
      } else if (!address.trim()) {
        toast.success("Base address cleared");
      } else {
        toast.success("Base address saved & geocoded");
      }
      await reload?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to save base address");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="bg-card border border-border rounded-[8px] p-6 space-y-3 mt-4"
      data-testid="settings-base-address"
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-lg font-extrabold flex items-center gap-2">
            <MapPin className="h-4 w-4" /> Base / depot address (for mileage reports)
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-prose">
            Premium feature — adds outbound + return legs to the mileage report so each driver&apos;s
            day starts at the depot and ends at the depot. Drivers with their own base address
            override this value.
          </p>
        </div>
        {hasCoords ? (
          <span className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="base-address-geocoded-pill">
            <CheckCircle2 className="h-3 w-3" /> Geocoded
          </span>
        ) : address ? (
          <span className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="base-address-no-coords-pill">
            <ShieldAlert className="h-3 w-3" /> No coords
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
        <Field label="Address" testId="base-address-line">
          <Textarea
            rows={2}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="e.g. Dublin Port, North Wall Quay, Dublin 1, IE"
            data-testid="base-address-input"
          />
        </Field>
        <Field label="Postcode" testId="base-address-postcode">
          <Input
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="D01 W2T1"
            data-testid="base-postcode-input"
          />
        </Field>
      </div>
      {hasCoords && (
        <div className="text-[11px] mono tabular-nums text-muted-foreground">
          {Number(data.base_lat).toFixed(6)}, {Number(data.base_lng).toFixed(6)}
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <Button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid="base-address-save-btn"
        >
          {saving ? "Saving…" : "Save & geocode"}
        </Button>
        {address && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => { setAddress(""); setPostcode(""); save(); }}
            disabled={saving}
            data-testid="base-address-clear-btn"
          >
            Clear
          </Button>
        )}
      </div>
    </section>
  );
}


// ============================================================================
// Phase P.15 — Products / Stock section (manager settings)
// ============================================================================
function ProductsSection() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product or {} for new
  // Phase P.27.1 — shadcn AlertDialog replacing the native archive-confirm.
  const [archiveConfirm, setArchiveConfirm] = useState(null);
  const doArchiveProduct = async (p) => {
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product archived");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setArchiveConfirm(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", {
        params: { q: q || undefined, include_archived: includeArchived },
      });
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q, includeArchived]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="settings-products-section">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Input
          placeholder="Search name, SKU, category…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
          data-testid="products-search"
        />
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
          Show archived
        </label>
        <Button
          size="sm"
          onClick={() => setEditing({})}
          data-testid="products-add-btn"
          className="ml-auto"
        >
          + Add product
        </Button>
      </div>
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-[8px] p-8 text-center text-sm text-muted-foreground">
          No products yet. Add your first product so staff can attach parts to jobs.
        </div>
      ) : (
        <div className="bg-card border border-border rounded-[8px] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 mono text-[10px] uppercase tracking-widest">Name</th>
                <th className="text-left px-3 py-2 mono text-[10px] uppercase tracking-widest">SKU</th>
                <th className="text-right px-3 py-2 mono text-[10px] uppercase tracking-widest">Price</th>
                <th className="text-right px-3 py-2 mono text-[10px] uppercase tracking-widest">Tax</th>
                <th className="text-right px-3 py-2 mono text-[10px] uppercase tracking-widest">Stock</th>
                <th className="text-right px-3 py-2 w-24"> </th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-border" data-testid={`product-row-${p.id}`}>
                  <td className="px-3 py-2 font-bold">
                    {p.name} {p.archived && <span className="ml-1 text-[10px] mono uppercase text-muted-foreground">(archived)</span>}
                    {p.description && <div className="text-[11px] text-muted-foreground font-normal">{p.description}</div>}
                  </td>
                  <td className="px-3 py-2 mono text-xs text-muted-foreground">{p.sku || "—"}</td>
                  <td className="px-3 py-2 text-right mono tabular-nums">€{(p.unit_price_cents / 100).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right mono tabular-nums text-xs">{(p.tax_rate * 100).toFixed(1)}%</td>
                  <td className={`px-3 py-2 text-right mono tabular-nums font-bold ${p.qty_on_hand < 0 ? "text-rose-700" : ""}`}>
                    {p.qty_on_hand.toFixed(0)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={() => setEditing(p)} data-testid={`product-edit-${p.id}`}>Edit</Button>
                      {!p.archived && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-rose-700"
                          onClick={() => setArchiveConfirm(p)}
                          data-testid={`product-archive-${p.id}`}
                        >
                          Archive
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <ProductEditDialog
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      <ConfirmDialog
        state={archiveConfirm && {
          title: "Archive product?",
          description: <>Archive <span className="font-semibold">&lsquo;{archiveConfirm.name}&rsquo;</span>?</>,
          subtitle: "Usage history is preserved — you can restore it later from the Archived tab.",
          confirmLabel: "Archive",
          destructive: true,
          testId: "product-archive-confirm",
          onConfirm: () => doArchiveProduct(archiveConfirm),
        }}
        onOpenChange={() => setArchiveConfirm(null)}
      />
    </div>
  );
}

function ProductEditDialog({ product, onClose, onSaved }) {
  const isNew = !product?.id;
  const [f, setF] = useState({
    name: product?.name || "",
    description: product?.description || "",
    unit_price_cents: product?.unit_price_cents ?? 0,
    tax_rate: product?.tax_rate ?? 0.23,
    qty_on_hand: product?.qty_on_hand ?? 0,
    sku: product?.sku || "",
    category: product?.category || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const body = {
        name: f.name.trim(),
        description: f.description,
        unit_price_cents: Math.round(Number(f.unit_price_cents) || 0),
        tax_rate: Number(f.tax_rate) || 0,
        qty_on_hand: Number(f.qty_on_hand) || 0,
        sku: f.sku || null,
        category: f.category || null,
      };
      if (isNew) await api.post("/products", body);
      else await api.patch(`/products/${product.id}`, body);
      toast.success(isNew ? "Product created" : "Product updated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="product-edit-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add product" : "Edit product"}</DialogTitle>
          <DialogDescription>Parts, consumables, or anything you charge as a line item.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="product-form-name" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Unit price (cents)</Label>
              <Input type="number" value={f.unit_price_cents} onChange={(e) => setF({ ...f, unit_price_cents: e.target.value })} data-testid="product-form-price" />
              <div className="text-xs text-muted-foreground mt-1">= €{((Number(f.unit_price_cents) || 0) / 100).toFixed(2)}</div>
            </div>
            <div>
              <Label>Tax rate</Label>
              <select
                className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background"
                value={f.tax_rate}
                onChange={(e) => setF({ ...f, tax_rate: Number(e.target.value) })}
              >
                <option value="0.23">23% (VAT standard)</option>
                <option value="0.135">13.5% (Reduced)</option>
                <option value="0">0% (RCT / exempt)</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Qty on hand</Label>
              <Input type="number" value={f.qty_on_hand} onChange={(e) => setF({ ...f, qty_on_hand: e.target.value })} data-testid="product-form-qty" />
            </div>
            <div>
              <Label>SKU</Label>
              <Input value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} />
            </div>
            <div>
              <Label>Category</Label>
              <Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="product-form-save">
            {busy ? "Saving…" : (isNew ? "Create" : "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Phase P.15 — Staff-proposed template review section
// ============================================================================
function TemplateProposalsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [rejectFeedback, setRejectFeedback] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/template-proposals");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (p) => {
    try {
      await api.post(`/template-proposals/${p.id}/approve`);
      toast.success(`Approved — template '${p.name}' created`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  const reject = async () => {
    if (!reviewing) return;
    try {
      await api.post(`/template-proposals/${reviewing.id}/reject`, { feedback: rejectFeedback });
      toast.success("Proposal rejected");
      setReviewing(null);
      setRejectFeedback("");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const pendingCount = items.filter((x) => x.status === "pending").length;
  return (
    <div data-testid="template-proposals-section">
      <div className="mb-3 flex items-center gap-2 text-sm">
        <span className="font-bold">Pending review:</span>
        <span className={`mono text-xs px-2 py-0.5 rounded-full ${pendingCount > 0 ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"}`} data-testid="template-proposals-pending-count">
          {pendingCount}
        </span>
      </div>
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-[8px] p-8 text-center text-sm text-muted-foreground">
          No proposals yet. Staff can suggest new templates from a job in their portal.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((p) => (
            <div key={p.id} className="bg-card border border-border rounded-[8px] p-4" data-testid={`template-proposal-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold">{p.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    from <span className="font-semibold">{p.proposed_by_staff_name || "staff"}</span> · {new Date(p.created_at).toLocaleString()}
                  </div>
                  {p.description && <div className="text-sm mt-2">{p.description}</div>}
                  {p.reason && (
                    <div className="text-xs text-muted-foreground italic mt-1">
                      Reason: {p.reason}
                    </div>
                  )}
                  {(p.unit_price_cents || p.tax_rate !== null) && (
                    <div className="mono text-[11px] mt-1 text-muted-foreground">
                      Suggested: €{((p.unit_price_cents || 0) / 100).toFixed(2)}
                      {p.tax_rate !== null && p.tax_rate !== undefined && (
                        <> · tax {(p.tax_rate * 100).toFixed(1)}%</>
                      )}
                    </div>
                  )}
                </div>
                <div className="shrink-0 flex items-center gap-1">
                  {p.status === "pending" ? (
                    <>
                      <Button size="sm" onClick={() => approve(p)} data-testid={`template-proposal-approve-${p.id}`}>Approve</Button>
                      <Button variant="outline" size="sm" className="text-rose-700" onClick={() => setReviewing(p)} data-testid={`template-proposal-reject-${p.id}`}>Reject</Button>
                    </>
                  ) : (
                    <span className={`mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${p.status === "approved" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}>
                      {p.status}
                    </span>
                  )}
                </div>
              </div>
              {p.review_feedback && (
                <div className="text-xs italic text-muted-foreground mt-2 pt-2 border-t border-border">
                  Feedback: {p.review_feedback}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {reviewing && (
        <Dialog open onOpenChange={(o) => { if (!o) { setReviewing(null); setRejectFeedback(""); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject proposal</DialogTitle>
              <DialogDescription>Optional feedback to the staff member.</DialogDescription>
            </DialogHeader>
            <Input
              placeholder="e.g. Too similar to 'Pump Hire — Standard'"
              value={rejectFeedback}
              onChange={(e) => setRejectFeedback(e.target.value)}
              autoFocus
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setReviewing(null); setRejectFeedback(""); }}>Cancel</Button>
              <Button className="bg-rose-600 hover:bg-rose-700" onClick={reject}>Reject proposal</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

